import ISelect from "../../../common/models/ISelect";
import IStaticDataValue from "../../../common/models/IStaticDataValue";
import { IForceCloseReasons } from "../models/IForceCloseReasons";
import { IFeatureConfigKeySearchMap } from "../models/IFeatureConfig";

export interface SpecialityInfo {
    id: string,
    display: string
}

export enum DOCTOR_TYPES_FOR_APPOINTMENT {
    GP = "GP",
    PSYCHIATRIST = "PSYCHIATRIST"
}

export enum APP_TENANTS {
    SUGARFIT = "SUGARFIT",
    TRANSFORM = "TRANSFORM",
    CAREFIT = "CAREFIT"
}

export class AppConstants {
    static NUMBER_OF_RECOM_TEMPLATES_TO_FETCH = 5;
    static IN_APP_NOTIFICATION = {
        POLL_INTERVAL_IN_MS: 15000,
        MIN_INTERVAL_BETWEEN_APPOINTEMENTS_FOR_ALERT: 10 * 60 * 1000
    }

    static GLUCOSE_CHRONIC_CARE_METRIC_ID = 1679;
    static ONGOING_CONS_OPEN_EVENT = "ONGOING_CONS_OPEN_EVENT";
    static SUGAR_FIT_TENANT = "SUGAR_FIT_APP";

    static ROSTER_MESSAGES = {
        CONFLICTING_ROSTER: "Current roster conflicts with existing shifts on ",
        CONFLICTINF_SHIFTS_IN_DAY: "You have added conflicting shifts.",
        INVALID_INTERVAL: "Shift end time should not be less than start time.",
        NULL_SHIFTS: "Please select time for all the shifts.",
        SAME_TIME: "Shift start and end time should not be same.",
        LATE_SHIFT: "Selected working hours fall late in the night.. Please proceed only if you are certain that you will join the consultations.",
        NON_OPERATIONAL_HOURS: "The selected time is not within operational hours (7AM - 11PM)."
    }
    static SLOW_NETWORK_MESSAGE: string = "Slow/Unstable Internet detected.";
    static SLOW_NETWORK_MESSAGE_CLEAR_INTERVAL_MS: number = 10000;
    static SLOW_NETWORK_THRESHOLD: number = 2;

    static RESOURCE_TYPE_FOR_ROSTER: string = "DOCTOR";


    static EPOCH_MS_IN_ONE_DAY = 24 * 60 * 60 * 1000;

    static LOG_INTERVAL_IN_MS = 60000;
    static HOUR_CELL_HEIGHT_IN_PIXEL: number = 150;
    static PRESCRIPTION_MAX_HEIGHT = 620;
    static PHYSIO_PRESCRIPTION_MAX_HEIGHT = 540;
    static DIET_PLAN_MAX_HEIGHT = 540;
    static PLAN_PRESCRIPTION_MAX_HEIGHT = 580;
    static DAYS_IN_A_WEEK: number = 7;
    static CONSULTATION_START_THRESHOLD_IN_MIN = 240;
    static CONSULTATION_START_THRESHOLD_AFTER_TIME_IN_MIN = 50;
    static CONSULTATION_DELAY_HOOKS_IN_MINUTES = [5, 10, 15];
    static CONSULTATION_DELAY_HOOKS_IN_MINUTES_FOR_LC = [30, 45];
    static readonly MEDICINE_PRESCRIPTION_TABLE_HEADER_ARRAY: string[] = ["MEDICINE", "FREQUENCY", "DURATION", "CONDITION", "INSTRUCTIONS"];
    static TIME_OF_DAY = ["Morning", "Noon", "Afternoon", "Evening", "Night"];
    static FETCH_MEDICAL_PROFILE_FOR_NUMBER_OF_DAYS_DEFAULT = 180;
    static UNREAD_MESSAGE_FOR_NUMBER_OF_DAYS = 100;
    static UNREAD_MESSAGE_TO_TIME_RECONCILIATION_IN_MINUTES = 5;
    static CURRENT_DATE = new Date().toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" });

    // API TIMEOUTS
    static UPDATE_APPOINTMENT_STATUS_TIMEOUT_IN_SECONDS = 5;
    static PDF_GENERATION_TIMEOUT_IN_SECONDS = 60;
    static FETCH_PATIENT_PERSONAL_PROFILE_TIMEOUT_IN_SECONDS = 5;
    static FETCH_CARE_RECOMMENDATIONS_TIMEOUT_IN_SECONDS = 1;

    // API RETRIES
    static UPDATE_APPOINTMENT_STATUS_MAX_RETRIES_COUNT = 3;

    // FETCH INTERVALS
    static FETCH_UNREAD_MESSAGE_INFO_IN_SECONDS = 30;

    static DEFAULT_POLLING_INTERVAL_IN_SECONDS = 30;

    static PAIN_SCALE_MAX_VALUE = 10;

    static MAX_CONSULTATIONS_FETCH_COUNT: number = 200;

    static ConsultationModes: { [key: string]: any } = {
        ONLINE: { id: "ONLINE", display: "Online" },
        OFFLINE: { id: "OFFLINE", display: "In Center" },
        INCENTRE: { id: "INCENTRE", display: "In Centre" }
    }

    static ConsultationCategory: { [key: string]: string } = {
        ONLINE: "CF_ONLINE_CONSULTATION",
        KMH: "KMH",
        HCU: "HCU",
        HCU_PACK: "HCU_PACK",
        MP: "MP"
    }

    static ConsultationTypes: { [key: string]: string } = {
        KHM: "KMH",
        HCU: "HCU",
        TC: "TC"
    }

    static ConsultationStatus: { [key: string]: string } = {
        WAITING_FOR_CONFIRMATION: "Awaiting confirmation",
        BOOKED: "Booked",
        SCHEDULED: "Scheduled",
        ARRIVED_INCENTRE: "Arrived @centre",
        VITALS_COLLECTED: "Vitals collected",
        CALL_REMINDER: "Call reminder",
        CONSULTATION_STARTED: "Consultation started",
        ONGOING: "Ongoing",
        PRESCRIPTION_GENERATED: "Prescription generated",
        PRESCRIPTION_IN_DRAFT: "Prescription in draft",
        CONSULTATION_COMPLETED: "Consultation completed",
        EDIT_PRESCRIPTION_GENERATED: "Edited Prescription generated",
        EDIT_PRESCRIPTION_COMPLETED: "Edited Prescription completed",
        CANCELLED: "Cancelled",
        RESCHEDULED: "Resceduled",
        COMPLETED: "Completed",
        MISSED: "Missed"
    }

    static RelativeDayLabelConstant: { [key: string]: any } = {
        TODAY: "Today",
        YESTERDAY: "Yesterday",
        TOMORROW: "Tomorrow"
    }

    static WeekdaysConstant: { [key: string]: any } = {
        SUN: "SUNDAY",
        MON: "MONDAY",
        TUE: "TUESDAY",
        WED: "WEDNESDAY",
        THU: "THURSDAY",
        FRI: "FRIDAY",
        SAT: "SATURDAY"
    }

    static MedicineInstructionCodes: { [key: string]: string } = {
        SOS: "As and when required",
        BEFORE_SLEEP: "Before sleep",
        BELOW_THE_TONGUE: "Below the tongue",
        BEFORE_FOOD: "Before food",
        AFTER_FOOD: "After food",
        EMPTY_STOMACH: "Empty stomach",
        CHEWABLE: "Chewable",
        PER_ORAL: "Per Oral",
        INTRADERMAL_INJECTION: "Intradermal Injection",
        INTRAMUSCULAR: "Intramuscular",
        INTRAVENOUS: "Intravenous",
        SUBCUTANEOUS: "Subcutaneous",
        NASAL_SPRAY: "Nasal Spray",
        PER_VAGINAL: "Per Vaginal",
        PER_RECTUM: "Per Rectum",
        LOCAL_APPLICATION: "Local Application",
        SUBLINGUAL: "Sublingual",
        TRANSDERMAL: "Transdermal"
    }

    static monthsConstant: { [key: string]: any } = {
        JANUARY: "January",
        FEBRUARY: "February",
        MARCH: "March",
        APRIL: "April",
        MAY: "May",
        JUNE: "June",
        JULY: "July",
        AUGUST: "August",
        SEPTEMBER: "September",
        OCTOBER: "October",
        NOVEMBER: "November",
        DECEMBER: "December"
    }

    static SUBSECTION_ADD_PLACEHOLDERS: { [key: string]: string | undefined } = {
        "prescriptionDiagnosis": "diagnosis",
        "prescriptionLabTest": "lab test",
        "prescriptionMedicines": "medicine",
        "prescriptionAdvice": "advice",
        "allergies": "allergy",
        "pastConditions": "past condition",
        "surgery": "surgery",
        "familyHistory": "family history",
        "currentMedication": "current medication"
    }

    static SpecialityList: SpecialityInfo[] = [
        { id: "CARDIO", display: "Cardiologist" },
        { id: "ENT", display: "Ear Nose Throat" },
        { id: "DENTIST", display: "Dentist" },
        { id: "DERMA", display: "Dermatologist" },
        { id: "GYNAEC", display: "Gynecologist" },
        { id: "PEDS", display: "Peditrician" },
        { id: "GASTRO", display: "Gastroenterologist" },
        { id: "NEURO", display: "Neurologist" }
    ]

    static LabtestConditions: { [key: string]: any } = {
        NONE: "None",
        RANDOM: "Random",
        FASTING: "Fasting",
        SPECIAL_INSTRUCTIONS: "Special Instructions"
    }

    static LabtestUnits: { [key: string]: any } = {
        "mmHg": "mmHg",
        "mg/dl": "mg/dl",
        "g/dl": "g/dl",
        "mIU/L": "mIU/L",
        "l/min": "l/min",
        "ng/ml": "ng/ml",
        "IU/ml": "IU/ml",
        "mg/L": "mg/L",
        "mm/ hr.": "mm/ hr.",
        "uU/ml": "uU/ml",
        "ug/dl": "ug/dl",
        "mg/l": "mg/l",
        "mmol/L": "mmol/L",
        "ng/mL": "ng/mL",
        "ug/dL": "ug/dL",
        "%": "%"
    }

    static LabtestWhen: { [key: string]: any } = {
        WEEK1: "1 week",
        WEEK2: "2 weeks"
    }

    static FrequencyUnit: { [key: string]: string } = {
        HOUR: "Hour",
        DAY: "Day",
        MONTH: "Month",
        YEAR: "Year",
        WEEK: "Week"
    }

    static TimeFrequencyUnit: ISelect[] = [
        { displayValue: AppConstants.FrequencyUnit.DAY, internalValue: "DAY" },
        { displayValue: AppConstants.FrequencyUnit.WEEK, internalValue: "WEEK" },
        { displayValue: AppConstants.FrequencyUnit.MONTH, internalValue: "MONTH" },
        { displayValue: AppConstants.FrequencyUnit.YEAR, internalValue: "YEAR" }
    ]

    static PatientRelationshipConstant: { [key: string]: IStaticDataValue } = {
        SELF: { displayValue: "Self", internalValue: "SELF" },
        FATHER: { displayValue: "Father", internalValue: "FATHER" },
        MOTHER: { displayValue: "Mother", internalValue: "MOTHER" },
        BROTHER: { displayValue: "Brother", internalValue: "BROTHER" },
        SISTER: { displayValue: "Sister", internalValue: "SISTER" },
        UNCLE: { displayValue: "Uncle", internalValue: "UNCLE" },
        AUNTY: { displayValue: "Aunty", internalValue: "AUNTY" },
        PATERNAL_GRANDFATHER: { displayValue: "Paternal Grandfather", internalValue: "PATERNAL_GRANDFATHER" },
        PATERNAL_GRANDMOTHER: { displayValue: "Paternal Grandmother", internalValue: "PATERNAL_GRANDMOTHER" },
        MATERNAL_GRANDFATHER: { displayValue: "Maternal Grandfather", internalValue: "MATERNAL_GRANDFATHER" },
        MATERNAL_GRANDMOTHER: { displayValue: "Maternal Grandmother", internalValue: "MATERNAL_GRANDMOTHER" }
    }

    static ProfessionList: ISelect[] = [
        { displayValue: "Doctor", internalValue: "Doctor" },
        { displayValue: "Teacher", internalValue: "Teacher" },
        { displayValue: "Artist", internalValue: "Artist" },
        { displayValue: "Software Engineer", internalValue: "Software Engineer" }
    ]

    static WeekDayList: ISelect[] = [
        { displayValue: "M", internalValue: AppConstants.WeekdaysConstant.MON },
        { displayValue: "T", internalValue: AppConstants.WeekdaysConstant.TUE },
        { displayValue: "W", internalValue: AppConstants.WeekdaysConstant.WED },
        { displayValue: "T", internalValue: AppConstants.WeekdaysConstant.THU },
        { displayValue: "F", internalValue: AppConstants.WeekdaysConstant.FRI },
        { displayValue: "S", internalValue: AppConstants.WeekdaysConstant.SAT },
        { displayValue: "S", internalValue: AppConstants.WeekdaysConstant.SUN },
    ]

    static TimeList: ISelect[] = [
        { displayValue: "AM", internalValue: "AM" },
        { displayValue: "PM", internalValue: "PM" },
    ]

    static YesNoToggleList: ISelect[] = [
        { displayValue: "Yes", internalValue: true },
        { displayValue: "No", internalValue: false }
    ]

    static Templates: { [key: string]: string } = {
        PATIENT_PRESCRIPTION: "patient-prescription-template",
        PATIENT_PRESCRIPTION_PREVIEW: "prescription-preview-template",
        PATIENT_PLAN: "patient-plan-template",
        PATIENT_PLAN_PREVIEW: "plan-preview-template",
    }

    static DiagnosisType: { [key: string]: string } = {
        PROVISIONAL: "PROVISIONAL",
        FINAL: "FINAL"
    }

    static allergenTypes: { [key: string]: { displayValue: string, internalValue: string } } = {
        food: { displayValue: "Food", internalValue: "food" },
        environmental: { displayValue: "Environmental", internalValue: "environmental" },
        drug: { displayValue: "Drugs", internalValue: "drug" }
    }

    static pdfAttachmentType: { [key: string]: string } = {
        EMAIL_ATTACHMENT: "EMAIL_PRESCRIPTION",
        PRINT_ATTACHMENT: "PRINT_PRESCRIPTION",
        EMAIL_PLAN_ATTACHMENT: "EMAIL_PLAN",
        PRINT_PLAN_ATTACHMENT: "PRINT_PLAN"
    }

    static pdfAttachmentAction: { [key: string]: string } = {
        EMAIL: "EMAIL",
        PRINT: "PRINT"
    }

    static mentalStatusExamToggleValues = [
        { displayValue: "Normal", internalValue: "NORMAL" },
        { displayValue: "Abnormal", internalValue: "ABNORMAL" }
    ]

    static planPdfAttachmentType: { [key: string]: string } = {
        EMAIL_ATTACHMENT: "EMAIL_PLAN",
        PRINT_ATTACHMENT: "PRINT_PLAN"
    }

    static DietRecallMealSlots: IStaticDataValue[] = [
        {
            displayValue: "Pre Breakfast",
            internalValue: "PRE_BREAKFAST"
        }, {
            displayValue: "Breakfast",
            internalValue: "BREAKFAST"
        }, {
            displayValue: "Mid Morning Snack",
            internalValue: "MID_MORNING"
        }, {
            displayValue: "Early Afternoon Snack",
            internalValue: "EARLY_AFTERNOON"
        }, {
            displayValue: "Lunch",
            internalValue: "LUNCH"
        }, {
            displayValue: "Evening Snack",
            internalValue: "EVENING"
        }, {
            displayValue: "Late Evening Snack",
            internalValue: "LATE_EVENING"
        }, {
            displayValue: "Pre Workout",
            internalValue: "PRE_WORKOUT"
        }, {
            displayValue: "Post Workout",
            internalValue: "POST_WORKOUT"
        }, {
            displayValue: "Dinner",
            internalValue: "DINNER"
        }, {
            displayValue: "Bedtime Snack",
            internalValue: "BEDTIME"
        }]

    static patientRelationships: { [key: string]: string } = {
        "SELF": "SELF"
    }

    static planStatus: { [key: string]: string } = {
        DRAFT: "DRAFT",
        CANCELLED: "CANCELLED",
        LIVE: "LIVE"
    }

    static MPDoctorTypes: { [key: string]: string } = {
        DOCTOR: "DOCTOR",
        THERAPIST: "THERAPIST",
        PSYCHIATRIST: "PSYCHIATRIST",
        LIFESTYLE: "LIFESTYLE",
        MIND_THERAPIST: "MIND_THERAPIST"
    }

    static dateFormat: string = "DD-MM-YYYY"

    static reverseDateFormat: string = "YYYY-MM-DD"

    static goalTargetAims: { [key: string]: string } = {
        MANAGE: "Maintain",
        INCREASE: "Increase",
        DECREASE: "Decrease"
    }

    static PainRelievingFactorOptions: ISelect[] = [
        { displayValue: "Scalp", internalValue: "scalp" },
        { displayValue: "Eating habits", internalValue: "eating habits" },
        { displayValue: "Environment", internalValue: "environment" }
    ]

    static GCM_STATUS_LOCAL_STORAGE_KEY = "careGcmStatus_v2"
}

export enum EditPrescriptionTabKeyName {
    EDIT_PRESCRIPTION = "EDIT_PRESCRIPTION"
}

export const trackConfig: any = {
    servicesToTrack: ["dummy/track"],
    serviceUrl: "/orion/client/dummy/track?ts=rk",
    maxLatencyAllowedInSec: 1.8
};


export const vitalHeaderMap: { [key: string]: string } = {
    "BMI": "BMI",
    "BP": "BP",
    "COMBINED BP": "BP",
    "SYSTOLIC BP": "SYSTOLIC BP",
    "DIASTOLIC BP": "DIASTOLIC BP",
    "PULSE RATE": "PR",
    "RESPIRATORY RATE": "RR",
    "TEMPERATURE": "TEMP.",
    "SPO2": "SPO2",
    "WEIGHT": "WT",
    "HEIGHT": "HT",
    "WAIST": "WAIST",
    "HIP": "HIP",
    "WHR": "WHR"
}

export const APP_STYLE_CONSTANTS = {
    APP_NAV: 65,
    TABS_NAV: 55
}

export const SCHEMA_TYPE = {
    ARRAY: "array",
    OBJECT: "object"
}

export enum JOIN_UNJOIN_CALL_EVENTS {
    DOCTOR_JOINED_CALL = "DOCTOR_JOINED_CALL",
    DOCTOR_UNJOINED_CALL = "DOCTOR_UNJOINED_CALL"
}

export enum DosageSlots {
    BREAKFAST = "BREAKFAST",
    LUNCH = "LUNCH",
    DINNER = "DINNER"
}

export const MealSlots: { [key: string]: string } = {
    "PRE_BREAKFAST": "Pre Breakfast",
    "BREAKFAST": "Breakfast",
    "LUNCH": "Lunch",
    "MID_DAY_MEAL": "Mid day meal",
    "SNACKS": "Evening Snacks",
    "PRE_WORKOUT": "Pre workout",
    "POST_WORKOUT": "Post workout",
    "DINNER": "Dinner",
    "EARLY_AFTERNOON_SNACK": "Early Afternoon Snack",
    "MID_MORNING_SNACK": "Mid Morning Snack",
    "LATE_EVENING_SNACK": "Late Evening Snack",
    "BEDTIME_SNACK": "Bedtime Snack"
}

export const FoodGroups: { [key: string]: string } = {
    "FRUITS": "Fruits",
    "CEREALS": "Cereals",
    "NON-VEG": "Non Veg",
    "PULSES": "Pulses",
    "NUTS": "Nuts",
    "MILK": "Milk",
    "VEGGIES": "Veggies",
    "BEVERAGES": "Beverages",
    "OTHERS": "Others"
}

export const FoodGroup: { [key: string]: string } = {
    "LENTILS": "Lentils/Pulses/Legumes",
    "WHOLEGRAINS": "Whole Grains",
    "MILK_PRODUCTS": "Milk Products",
    "NUTS_AND_SEEDS": "Nuts and Seeds",
    "VEGETABLES": "Vegetables",
    "LEAN_MEAT": "Lean Meat",
    "EGGS": "Eggs",
    "FRUITS": "Fruits",
    "VEGETABLES_OR_FRUITS": "Vegetables or Fruits",
    "LEAN_MEAT_OR_EGGS": "Lean Meat or Eggs",
    "LENTILS_OR_LEAN_MEAT": "Lentils or Lean Meat",
    "LENTILS_OR_MILK_PRODUCTS": "Lentils or Milk Products",
    "LENTILS_OR_EGGS": "Lentils or Eggs",
    "LEAN_MEAT_OR_MILK_PRODUCTS": "Lean Meat or Milk Products",
    "MILK_PRODUCTS_OR_LEAN_MEAT_OR_EGGS_OR_LENTILS": "Milk Products or Lean Meat or Eggs or Lentils",
    "FRUITS_OR_NUTS_AND_SEEDS": "Fruits or Nuts & Seeds",
    "LENTILS_OR_NUTS_AND_SEEDS": "Lentils or Nuts & Seeds",
    "CEREALS_OR_PULSES_OR_FRUITS_OR_VEGETABLES": "Cereals or Pulses or Fruits or Vegetables",
    "BEVERAGES": "Beverages"
};

export const FoodGroupsMapping: { [key: string]: string[] } = {
    "LENTILS": ["LENTILS"],
    "WHOLEGRAINS": ["WHOLEGRAINS"],
    "MILK_PRODUCTS": ["MILK_PRODUCTS"],
    "NUTS_AND_SEEDS": ["NUTS_AND_SEEDS"],
    "VEGETABLES": ["VEGETABLES"],
    "LEAN_MEAT": ["LEAN_MEAT"],
    "EGGS": ["EGGS"],
    "FRUITS": ["FRUITS"],
    "VEGETABLES_OR_FRUITS": ["VEGETABLES", "FRUITS"],
    "LEAN_MEAT_OR_EGGS": ["LEAN_MEAT", "EGGS"],
    "LENTILS_OR_LEAN_MEAT": ["LENTILS", "LEAN_MEAT"],
    "LENTILS_OR_MILK_PRODUCTS": ["LENTILS", "MILK_PRODUCTS"],
    "LENTILS_OR_EGGS": ["LENTILS", "EGGS"],
    "LEAN_MEAT_OR_MILK_PRODUCTS": ["LEAN_MEAT", "MILK_PRODUCTS"],
    "MILK_PRODUCTS_OR_LEAN_MEAT_OR_EGGS_OR_LENTILS": ["MILK_PRODUCTS", "LEAN_MEAT", "EGGS", "LENTILS"],
    "FRUITS_OR_NUTS_AND_SEEDS": ["FRUITS", "NUTS_AND_SEEDS"],
    "LENTILS_OR_NUTS_AND_SEEDS": ["LENTILS", "NUTS_AND_SEEDS"],
    "CEREALS_OR_PULSES_OR_FRUITS_OR_VEGETABLES": ["CEREALS", "PULSES", "FRUITS", "VEGETABLES"],
    "BEVERAGES": ["BEVERAGES"]
}

export const AppDisplayInfos = {
    UPCOMING_CONSULTATION_INFO: "Start Consultation",
    ADDITIONAL_INSTRUCTIONS_EDITVIEW_FOLLOW_UP_CHECKBOX_LABEL: "Follow Up Required?"
}

export const AppMessages = {
    SAVE_PRESCRIPTION_SUCCESSFUL: "Prescription details have been saved successfully",
    SAVE_PRESCRIPTION_UNSUCCESSFUL: "Prescription details could not be saved",
    SAVE_PRESCRIPTION_PDF_SUCCESSFUL: "Prescription PDF saved successfully",
    SAVE_PRESCRIPTION_PDF_UNSUCCESSFUL: "Prescription PDF details could not be saved",
    SAVE_PLAN_SUCCESSFUL: "Patient Plan has been saved successfully",
    SAVE_PLAN_UNSUCCESSFUL: "Patient Plan could not be saved",
    SAVE_PLAN_PDF_SUCCESSFUL: "Plan PDF saved successfully",
    SAVE_PLAN_PDF_UNSUCCESSFUL: "Plan PDF could not be saved",

    // MyPatientsPage
    MY_PATIENTS_PAGE_FETCHING_APPOINTMENTS: "Getting your appointments...",
    ALL_PATIENTS_PAGE_FETCHING_PATIENTS: "Getting your patients...",
    MY_PATIENTS_PAGE_NO_SYMPTOMS_FOUND: "No symptoms found",
    MY_PATIENTS_PAGE_END_OF_RESULTS: "No more consultations",

    // Chat Container
    CHAT_CONTAINER_FETHING_CHAT_DETAILS: "Please wait while we fetch the chat details",

    // Dashboard Screen
    DOCTOR_DASHBOARD_NO_SUMMARY_FOUND: "No summary",

    // Ongoing Consultation
    VITALS_SUMMARY_BAR_NO_VITALS: "No Vitals Recorded",
    VITALS_SUMMARY_BAR_NO_ALLERGIES: "No allergy data found",
    CHARTS_INSUFFICIENT_DATA: "Insufficient data to render charts",
    VITALS_AND_ALLERGIES_VALIDATION_ERROR: "Allergies validation failed",
    VITALS_AND_ALLERGIES_SAVE_ERROR: "Vitals and Allergies data could not be saved",
    LAB__TESTS_VALIDATION_ERROR: "Lab Tests validation failed",
    LAB_TESTS_SAVE_ERROR: "Lab Tests data could not be saved",
    CHIEF_COMPLAINTS_VALIDATION_ERROR: "Chief Complaints validation failed",
    CHIEF_COMPLAINTS_SAVE_ERROR: "Complaints and illnesses data could not be saved",
    VITALS_SAVE_ERROR: "Vitals information could not be saved",
    MEDICAL_HISTORY_VALIDATION_ERROR: "Medical history validation failed",
    MEDICAL_HISTORY_SAVE_ERROR: "Patient Medical history data could not be saved",
    PHYSICAL_ASSESSMENT_VALIDATION_ERROR: "Physical Assessment validation failed",
    PHYSICAL_ASSESSMENT_SAVE_ERROR: "Physical Assessment data could not be saved",
    PRESCRIPTION_VALIDATION_ERROR: "Patient Prescription validation failed",
    PRESCRIPTION_SAVE_ERROR: "Patient Prescription data could not be saved",
    EXAMINATION_HISTORY_SAVE_ERROR: "Patient Examination data could not be saved",
    LAB_TEST_UNKNOWN_TEST_ERROR_MESSAGE: "Unknown Test",
    PRESCRIPTION_PDF_SAVE_FAILURE: "Could not save Prescription PDF",
    GOALS_VALIDATION_ERROR: "Goals validation failed",
    GOALS_SAVE_ERROR: "Patient Goals could not be saved",
    LIFESTYLE_SAVE_ERROR: "Patient lifestyle data could not be saved",
    PLAN_VALIDATION_ERROR: "Patient Plan Validation Failed",
    EXAMINATIONS_SAVE_ERROR: "Patient Examinations data could not be saved",
    PATIENT_LIFESTYLE_PLAN_SAVE_ERROR: "Mind Plan data could not be saved",

    // Error Container
    ERROR_CONTAINER_SEND_TO_ROLLBAR: "Facing this issue often? Let our developers know about it",
    ERROR_CONTAINER_ROLLBAR_SUCCESS: "Error successfully logged",

    ERROR_SPECIAL_CHARACTERS: "String contains special characters",

    // Edit Prescription
    EDIT_PRESCRIPTION_CONFIRM_DIALOG: "Updating will share the latest prescription with the patient.",
    EDIT_PRESCRIPTION_CANCEL_DIALOG: "The changes you have made will not be saved to the prescription.",

    SAVE_PRIVATE_NOTES_SUCCESSFUL: "Notes have been saved successfully.",
    SAVE_PRIVATE_NOTES_UNSUCCESSFUL: "Error: Notes could not be saved."
}

export const AppPlaceholders = {
    GENERIC_FREQUENCY: "#times / unit",
    GENERIC_DURATION: "no. of d/w/m/y",
    SYMPTOMS_EDITVIEW_NAME: "Cold",
    MINDFULNESS_EDITVIEW_NAME: "Yoga",
    SYMPTOMS_EDITVIEW_FREQUENCY: "#times / unit",
    SYMPTOMS_EDITVIEW_ATTRIBUTES: "Yellow Mucous etc",
    SYMPTOMS_EDITVIEW_DAYS: "0",
    SYMPTOMS_EDITVIEW_ILLNESSES: "Enter descriptive text about patient illnesses",
    RECURRING_SYMPTOMS_TROUBLE: "e.g. Conjuctivitis",
    SURGERIES_EDITVIEW_NAME: "Surgery Name",
    SURGERIES_EDITVIEW_YEAR: "Surgery Year",
    SURGERIES_EDITVIEW_COMMENTS: "Add Comments",
    ALLERGY_EDITVIEW_TYPE: "Allergy type",
    ALLERGY_EDITVIEW_ALLERGEN: "Allergen",
    CURRENT_MEDICATION_EDITVIEW_NAME: "Medicine name",
    CURRENT_MEDICATION_EDITVIEW_DOSAGE: "mg",
    CURRENT_MEDICATION_EDITVIEW_FREQUENCY: "#times / unit",
    CURRENT_MEDICATION_EDITVIEW_DURATION: "number of units",
    CURRENT_MEDICATION_EDITVIEW_COMMENT: "Add comments",
    LIFESTYLE_ADVICE_EDITVIEW_NAME: "Walk 5 kms everyday",
    LIFESTYLE_ADVICE_EDITVIEW_INSTRUCTIONS: "Early in the morning, Empty stomach",
    DIAGNOSIS_EDITVIEW_CODE: "Disease / ICD code",
    DIAGNOSIS_EDITVIEW_NAME: "Type diagnosis detail here",
    DIAGNOSTIC_TEST_NAME: "Diagnostic Test",
    DIAGNOSIS_EDITVIEW_COMMENTS: "Add comments",
    LAB_TESTS_EDITVIEW_NAME: "Cholesterol, Blood Culture",
    LAB_TESTS_EDITVIEW_FREQUENCY_COUNT: "0",
    LAB_TESTS_EDITVIEW_RECORDED_VALUE: "Recorded Value",
    LAB_TESTS_EDITVIEW_INSTRUCTIONS: "Early in the morning, Empty Stomach",
    DIAGNOSTIC_LAB_TESTS_EDITVIEW_REMARKS: "Remarks",
    ADDITIONAL_INSTRUCTIONS_EDITVIEW_SPECIALIST: "Specialist",
    ADDITIONAL_INSTRUCTIONS_EDITVIEW_REASON: "Reason",
    ADDITIONAL_INSTRUCTIONS_EDITVIEW_WHEN: "No. of days",
    ADDITIONAL_INSTRUCTIONS_EDITVIEW_FOLLOW_UP: "No of days",
    ADDITIONAL_INSTRUCTIONS_EDITVIEW_FOLLOW_UP_SAFETY_NETTING: "Probable Symptoms",
    CONDITIONS_EDITVIEW_NAME: "Disease name",
    CONDITIONS_EDITVIEW_ONSET_AGE: "Onset Age",
    CONDITIONS_EDITVIEW_ATTRIBUTES: "Specifics (if any)",
    CONCERNS_EDITVIEW_TROUBLE: "Type Condition",
    CONCERNS_EDITVIEW_LAST_OCCURENCE: "Last Occurence",
    CONCERNS_EDITVIEW_LAST_OCCURENCE_UNIT: "No. D/M/Y",
    CONCERNS_EDITVIEW_FREQUENCY: "Times",
    CONCERNS_EDITVIEW_LAST_COMMENTS: "Add Comments",
    RED_FLAGS_EDITVIEW_COMMENTS: "Any Comments",
    PATIENT_NOTES: "Type notes here...",
    OTHERS: "Others",
    SYMPTOMS: "Symptoms",
    DIAGNOSIS: "depression, etc",
    NAME: "name",
    OBSERVATIONS: "Observations",
    ANY_OTHER_COMMENTS: "Any other comments",
    DIET_PLAN_EDITVIEW_THEME: "Enter a diet theme",
    DIET_PLAN_EDITVIEW_GUIDELINE: "Enter a diet guideline",
    HABIT_EDITVIEW_THEME: "Sleep well",
    DIET_PLAN_EDITVIEW_CALORIES: "Calories",
    FITNESS_PLAN_ACTIVITY: "Fitness Activity",
    FITNESS_PLAN_ADVICE: "Comments",
    WORKOUT_PREFERENCES_ACTIVITY: "Activity",
    WORKOUT_PREFERENCES_FREQUENCY: "No. of times",
    FILTER_MEAL: "Filter text",
    CUSTOM_MEAL_NAME: "Enter meal name",
    HEALTH_TARGET_OTHER_GOAL: "Enter a goal",
    DISH_UNIT: "Serving units"
}

export const AccessTokenRequestConstants = {
    commUserType: {
        DOCTOR: "DOCTOR",
        PATIENT: "PATIENT"
    },
    commMode: {
        CHAT: "CHAT",
        VIDEO: "VIDEO"
    },
    endPointId: {
        WEB: "web"
    }
}

export enum ConsultationStatusConstant {
    WAITING_FOR_CONFIRMATION = "WAITING_FOR_CONFIRMATION",
    BOOKED = "BOOKED",
    SCHEDULED = "SCHEDULED",
    MISSED = "MISSED",
    CUSTOMER_MISSED = "CUSTOMER_MISSED",
    ARRIVED_INCENTRE = "ARRIVED_INCENTRE",
    VITALS_COLLECTED = "VITALS_COLLECTED",
    CALL_REMINDER = "CALL_REMINDER",
    CONSULTATION_STARTED = "CONSULTATION_STARTED",
    ONGOING = "ONGOING",
    PRESCRIPTION_GENERATED = "PRESCRIPTION_GENERATED",
    EDIT_PRESCRIPTION_GENERATED = "EDIT_PRESCRIPTION_GENERATED",
    PRESCRIPTION_IN_DRAFT = "PRESCRIPTION_IN_DRAFT",
    CONSULTATION_COMPLETED = "CONSULTATION_COMPLETED",
    EDIT_PRESCRIPTION_COMPLETED = "EDIT_PRESCRIPTION_COMPLETED",
    CANCELLED = "CANCELLED",
    RESCHEDULED = "RESCHEDULED",
    COMPLETED = "COMPLETED",
    DOCTOR_UNAVAILABLE = "DOCTOR_UNAVAILABLE"
}

export enum UI_TABS_ID {
    CONSULTATION = "CONSULTATION",
    CONS_MP = "CONS_MP",
    LC = "LC",
    LC_MP = "LC_MP",
    AI_LC = "AI_LC",
    PHYSIO = "PHYSIO",
    MIND_THERAPIST = "MIND_THERAPIST",
    PSYCHIATRIST = "PSYCHIATRIST",
    AYURVEDA = "AYURVEDA"
}

export const prescriptionTabDefaultDoctorTypes: string[] = [UI_TABS_ID.CONSULTATION, UI_TABS_ID.CONS_MP, UI_TABS_ID.AYURVEDA]

export enum staticDataSource {
    CAS = "cas",
    STATIC = "static"
}

export enum UserRoles {
    DOCTOR = "DOCTOR",
    NURSE = "NURSE",
    ADMIN = "ADMIN"
}

export enum ExaminationConstant {
    GENERAL = "GENERAL",
    SYSTEMIC = "SYSTEMIC"
}

export enum SourceConstants {
    DOCSASPP = "DOCSAPP"
}

export enum SymptomTypes {
    CHIEF = "CHIEF",
    ASSOCIATED = "ASSOCIATED"
}

export enum MedicationTypes {
    ACTIVE = "Active",
    PRESCRIPTION = "Prescription"
}

export enum QuestionType {
    SELECT = "SELECT",
    FREQUENCY = "FREQUENCY",
    BOOLEAN_TOGGLE = "BOOLEAN_TOGGLE",
    BOOLEAN_TOGGLE_FREQUENCY = "BOOLEAN_TOGGLE_FREQUENCY",
    INPUT = "INPUT",
    SELECTABLE_TOGGLE_LIST = "SELECTABLE_TOGGLE_LIST",
    PILLS = "PILLS,",
    QUANTIFIABLE_PILLS = "QUANTIFIABLE_PILLS",
    TIME_CAPTURE = "TIME_CAPTURE",
    TIME_RANGE_CAPTURE = "TIME_RANGE_CAPTURE",
    NUMERIC_INPUT = "NUMERIC_INPUT",
    TYPEAHEAD_SELECT = "TYPEAHEAD_SELECT",
    TEMPLATED_LIST = "TEMPLATED_LIST",
    TEMPLATED_LIST_V3 = "TEMPLATED_LIST_V3",
    PAIN_SCALE = "PAIN_SCALE",
    QUESTIONNAIRE = "QUESTIONNAIRE",
    TEXTAREA = "TEXTAREA",
    TYPEAHEAD = "TYPEAHEAD",
    QUESTIONNAIRE_LIST = "QUESTIONNAIRE_LIST",
    TOGGLE_BUTTON = "TOGGLE_BUTTON",
    TOGGLE_LIST = "TOGGLE_LIST",
    SUGGESTION = "SUGGESTION",
    LABEL = "LABEL",
    CHECKBOX = "CHECKBOX",
    SWITCH_TOGGLE = "SWITCH_TOGGLE",
    TOGGLE = "TOGGLE",
    NOTES = "NOTES",
    LINK = "LINK",
    TABLE = "TABLE",
    SUGGESTION_LIST = "SUGGESTION_LIST",
    LABEL_LIST = "LABEL_LIST",
    SPACING_BAR = "SPACING_BAR",
    TEMPLATED_LIST_V2 = "TEMPLATED_LIST_V2",
    ADVANCED_DURATION = "ADVANCED_DURATION",
    TOGGLE_SWITCH = "TOGGLE_SWITCH",
    TREATMENT_PLAN_LIST = "TREATMENT_PLAN_LIST",
    RICH_TEXT = "RICH_TEXT",
    PAST_PRESCRIPTION_LIST = "PAST_PRESCRIPTION_LIST",
    LINE_DIVIDER = "LINE_DIVIDER"
}

export enum ActivityCategory {
    WORKOUT = "WORKOUT",
    MEDITATION = "MEDITATION",
    GENERIC = "GENERIC",
    EAT = "EAT",
    MEAL = "MEAL",
    MEDICINE = "MEDICINE",
    MIND = "MIND"
}

export enum sortOrder {
    ASCENDING = "ASC",
    DESCENDING = "DESC"
}

export enum ForceCloseReason {
    USER_DID_NOT_JOINED = "USER_DID_NOT_JOINED",
    USER_MISBEHAVED = "USER_MISBEHAVED",
    USER_LEFT_THE_CALL = "USER_LEFT_THE_CALL",
    BAD_CALL_QUALITY = "BAD_CALL_QUALITY",
    USER_LEFT = "USER_LEFT",
    OTHERS = "OTHERS",
    CONSULTATION_INCOMPLETE = "CONSULTATION_INCOMPLETE",
    NOT_A_TELECONSULT = "NOT_A_TELECONSULT",
    PAPER_PRESCRIPTION = "PAPER_PRESCRIPTION",
    NO_PATIENT_CONSENT = "NO_PATIENT_CONSENT",
}

export enum MISSED_REASON_CODE {
    SYSTEM = "SYSTEM"
}

export const StaticForceCloseReasons: IStaticDataValue[] = [
    { displayValue: "Paper Prescription", internalValue: "PAPER_PRESCRIPTION" },
    { displayValue: "User did not join", internalValue: "USER_DID_NOT_JOINED" },
    { displayValue: "User misbehaved", internalValue: "USER_MISBEHAVED" },
    { displayValue: "User left the call", internalValue: "USER_LEFT_THE_CALL" },
    { displayValue: "Bad Call Quality", internalValue: "BAD_CALL_QUALITY" },
    { displayValue: "User left", internalValue: "USER_LEFT" }
]

export const OnlineForceCloseReasons: IForceCloseReasons[] = [
    { reasonTitle: "User did not join", reasonSubtitle: "Patient can reschedule to a different slot", internalValue: ForceCloseReason.USER_DID_NOT_JOINED, status: ConsultationStatusConstant.MISSED },
    { reasonTitle: "Consultation could not be completed", reasonSubtitle: "Patient can reschedule to a different slot", internalValue: ForceCloseReason.CONSULTATION_INCOMPLETE, status: ConsultationStatusConstant.MISSED },
    { reasonTitle: "Not right for teleconsult", reasonSubtitle: "Patient will be refunded", internalValue: ForceCloseReason.NOT_A_TELECONSULT, status: ConsultationStatusConstant.MISSED },
    { reasonTitle: "Consumer misbehaved", reasonSubtitle: "Apologies. Appointment will be cancelled & refunded", internalValue: ForceCloseReason.USER_MISBEHAVED, status: ConsultationStatusConstant.MISSED }
]

export const IncentreForceCloseReasons: IForceCloseReasons[] = [
    { reasonTitle: "Consultation could not be completed", reasonSubtitle: "Patient can reschedule to a different slot", internalValue: ForceCloseReason.CONSULTATION_INCOMPLETE, status: ConsultationStatusConstant.MISSED },
    // { reasonTitle: "Paper Prescription", reasonSubtitle: "App not working", internalValue: ForceCloseReason.PAPER_PRESCRIPTION, status: ConsultationStatusConstant.COMPLETED }
]

export const ConfigConstants = {
    NEWCONFIGDATA: "currentConsultationData",
    OLDCONSULTATION: "oldConsultationData",
    CURRENTCONSULTATION: "currentConsultationData"
}

export const TwilioRoomEventsLog = {
    PATIENT_JOINED_IN_DOCSAPP: "PATIENT_JOINED_IN_DOCSAPP",
    PATIENT_ALREADY_JOINED_IN_DOCSAPP: "PATIENT_ALREADY_JOINED_IN_DOCSAPP",
    DOCTOR_JOINED_IN_DOCSAPP: "DOCTOR_JOINED_IN_DOCSAPP",
    DOCTOR_TRACK_ADDED_IN_DOCSAPP: "DOCTOR_TRACK_ADDED_IN_DOCSAPP",
    PATIENT_TRACK_ADDED_IN_DOCSAPP: "PATIENT_TRACK_ADDED_IN_DOCSAPP",
    PATIENT_TRACK_REMOVED_IN_DOCSAPP: "PATIENT_TRACK_REMOVED_IN_DOCSAPP",
    PATIENT_DISCONNECTED_IN_DOCSAPP: "PATIENT_DISCONNECTED_IN_DOCSAPP",
    DOCTOR_DISCONNECTED_IN_DOCSAPP: "DOCTOR_DISCONNECTED_IN_DOCSAPP",
    DOCTOR_LEAVING_ROOM_IN_DOCSAPP: "DOCTOR_LEAVING_ROOM_IN_DOCSAPP",
    DOCTOR_RECONNECTING_TO_TWILIO: "DOCTOR_RECONNECTING_TO_TWILIO",
    PATIENT_RECONNECTING_TO_TWILIO: "PATIENT_RECONNECTING_TO_TWILIO",
    AUDIO_PACKET_LOSS_IN_TWILIO: "AUDIO_PACKET_LOSS_IN_TWILIO",
    VIDEO_PACKET_LOSS_IN_TWILIO: "VIDEO_PACKET_LOSS_IN_TWILIO",
    AUDIO_VIDEO_PACKET_LOSS_IN_TWILIO: "AUDIO_VIDEO_PACKET_LOSS_IN_TWILIO",
    LOCAL_AUDIO_PACKET_LOSS_IN_TWILIO: "Local audio",
    LOCAL_VIDEO_PACKET_LOSS_IN_TWILIO: "Local video",
    REMOTE_AUDIO_PACKET_LOSS_IN_TWILIO: "Remote audio",
    REMOTE_VIDEO_PACKET_LOSS_IN_TWILIO: "Remote video",
    PACKET_LOSS_IN_ORION: "Packet loss in orion",
    NETWORK_QUALITY_CHANGED: "Network quality changed",
    MUTED: "Audio Muted",
    UNMUTED: "Audio Unmuted",
    VIDEO_DISABLED: "Video disabled",
    VIDEO_ENABLED: "Video Enabled",
    VIDEO_CALL_METRICS_INFO: "VIDEO_CALL_METRICS_INFO"

}

export const StaticGuidelines = [
    "Increase intake of Protein in Diet",
    "Increase intake of Fiber in Diet",
    "Increase intake of Complex Carbs in Diet",
    "Increase intake of Healthy Fat in Diet",
    "Increase intake of Plant Protein in Diet",
    "Increase intake of whole sources of carbohydrates eg wholegrains, starchy vegetables in Diet",
    "Limit intake of Protein in Diet",
    "Limit intake of Carbohydrates in Diet",
    "Limit intake of Fiber in Diet",
    "Limit intake of Fat in Diet",
    "Limit intake of Animal Protein in Diet",
    "Stop intake of Animal Protein in Diet",
    "Stop intake of hydrogenated oil in Diet",
    "Increase intake of Zinc in Diet",
    "Increase intake of Magnesium in Diet",
    "Increase intake of Calcium in Diet",
    "Increase intake of Iron in Diet",
    "Increase intake of Selenium in Diet",
    "Increase intake of Phosphorus in Diet",
    "Increase intake of Potassium in Diet",
    "Increase intake of Vitamin A in Diet",
    "Increase intake of Vitamin B in Diet",
    "Increase intake of Vitamin C in Diet",
    "Increase intake of Vitamin D in Diet",
    "Increase intake of Vitamin E in Diet",
    "Increase intake of Vitamin K in Diet",
    "Increase intake of Manganese in Diet",
    "Increase intake of Vegetables  In Diet",
    "Increase intake of Pulses & Lentils  In Diet",
    "Increase intake of Fruits In Diet",
    "Increase intake of Wholegrains  In Diet",
    "Increase intake of Nuts & Seeds In Diet",
    "Eat Fruits post waking up",
    "Increase intake of Vegetables  in breakfast",
    "Increase intake of Pulses & Lentils  in breakfast",
    "Increase intake of Fruits in breakfast",
    "Increase intake of Wholegrains  in breakfast",
    "Increase intake of Nuts & Seeds in breakfast",
    "Eat Vegetables  in pre-lunch snack",
    "Eat Pulses & Lentils  in pre-lunch snack",
    "Increase intake of Fruits in pre-lunch snack",
    "Increase intake of Nuts & Seeds in pre-lunch snack",
    "Increase intake of Vegetables  in lunch",
    "Increase intake of Pulses & Lentils  in lunch",
    "Increase intake of Wholegrains  in lunch",
    "Increase intake of Nuts & Seeds in lunch",
    "Increase intake of Vegetables  in snacks",
    "Increase intake of Pulses & Lentils  in snacks",
    "Increase intake of Fruits in snacks",
    "Increase intake of Wholegrains  in snacks",
    "Increase intake of Nuts & Seeds in snacks",
    "Increase intake of Vegetables  in dinner",
    "Increase intake of Pulses & Lentils  in dinner",
    "Increase intake of Fruits in dinner",
    "Increase intake of Wholegrains  in dinner",
    "Increase intake of Nuts & Seeds in dinner",
    "Increase intake of Fruits post-workout",
    "Increase intake of Nuts & Seeds post-workout",
    "Increase intake of Fruits pre-workout",
    "Increase intake of Nuts & Seeds pre-workout",
    "Limit intake of Non-Vegetarian Foods  In Diet",
    "Limit intake of Dairy  In Diet",
    "Limit intake of Wholegrains  In Diet",
    "Limit intake of Nuts & Seeds In Diet",
    "Limit intake of Non-Vegetarian Foods  in breakfast",
    "Limit intake of Dairy  in breakfast",
    "Limit intake of Dairy  in lunch",
    "Limit intake of Fruits in lunch",
    "Limit intake of Wholegrains  in lunch",
    "Limit intake of Nuts & Seeds in lunch",
    "Limit intake of Non-Vegetarian Foods  in dinner",
    "Limit intake of Pulses & Lentils  in dinner",
    "Limit intake of Dairy  in dinner",
    "Limit intake of Fruits in dinner",
    "Limit intake of Wholegrains  in dinner",
    "Limit intake of Nuts & Seeds in dinner",
    "Limit intake of Dairy  before sleep",
    "Limit intake of Dairy  post-workout",
    "Limit intake of Pulses & Lentils  pre-workout",
    "Limit intake of Dairy  pre-workout",
    "Stop intake of Non-Vegetarian Foods  In Diet",
    "Stop intake of Dairy  In Diet",
    "Stop intake of Dairy  post waking up",
    "Stop intake of Non-Vegetarian Foods  in breakfast",
    "Stop intake of Dairy  in breakfast",
    "Stop intake of Dairy  in pre-lunch snack",
    "Stop intake of Non-Vegetarian Foods  in lunch",
    "Stop intake of Non-Vegetarian Foods  in snacks",
    "Stop intake of Dairy  in snacks",
    "Stop intake of Fruits in dinner",
    "Stop intake of Vegetables  before sleep",
    "Stop intake of Non-Vegetarian Foods  before sleep",
    "Stop intake of Pulses & Lentils  before sleep",
    "Stop intake of Nuts & Seeds before sleep",
    "Increase intake of Starchy vegetables In Diet",
    "Increase intake of Green Leafy Vegetables In Diet",
    "Increase intake of Cruciferous Vegetables In Diet",
    "Increase intake of Roots & Tubers In Diet",
    "Increase intake of Lean Meat In Diet",
    "Increase intake of Citrus Fruits In Diet",
    "Increase intake of Berries In Diet",
    "Increase intake of Millets In Diet",
    "Eat Pseudo grains such as quinoa, amaranth, buckwheat",
    "Increase intake of sprouted lentils In Diet",
    "Increase intake of Green Leafy Vegetables in lunch",
    "Increase intake of Cruciferous Vegetables in lunch",
    "Eat Lean Meat in lunch",
    "Eat Split Lentils in lunch",
    "Eat Citrus Fruits in lunch",
    "Eat Berries in lunch",
    "Increase intake of Millets in lunch",
    "Increase intake of Pseudo grains such as quinoa, amaranth, buckwheat in lunch",
    "Increase intake of sprouted lentils in lunch",
    "Increase intake of Starchy vegetables in snacks",
    "Increase intake of Split Lentils in snacks",
    "Increase intake of Citrus Fruits in snacks",
    "Increase intake of Berries in snacks",
    "Increase intake of sprouted lentils in snacks",
    "Increase intake of Green Leafy Vegetables in dinner",
    "Increase intake of Cruciferous Vegetables in dinner",
    "Eat Lean Meat in dinner",
    "Eat Split Lentils in dinner",
    "Eat Citrus Fruits in dinner",
    "Eat Berries in dinner",
    "Eat Millets in dinner",
    "Eat Pseudo grains such as quinoa, amaranth, buckwheat in dinner",
    "Eat sprouted lentils in dinner",
    "Increase intake of Lean Meat post-workout",
    "Increase intake of Split Lentils post-workout",
    "Increase intake of Citrus Fruits post-workout",
    "Increase intake of Berries post-workout",
    "Increase intake of Millets post-workout",
    "Increase intake of sprouted lentils post-workout",
    "Limit intake of Starchy vegetables In Diet",
    "Limit intake of Green Leafy Vegetables In Diet",
    "Limit intake of Cruciferous Vegetables In Diet",
    "Limit intake of Roots & Tubers In Diet",
    "Limit intake of Goitrogen In Diet",
    "Limit intake of Red Meat In Diet",
    "Limit intake of Lean Meat In Diet",
    "Limit intake of Split Lentils In Diet",
    "Limit intake of Goitrogen in pre-lunch snack",
    "Limit intake of Red Meat in pre-lunch snack",
    "Limit intake of Goitrogen in lunch",
    "Limit intake of Red Meat in lunch",
    "Limit intake of Goitrogen in snacks",
    "Limit intake of Red Meat in snacks",
    "Limit intake of Goitrogen in dinner",
    "Limit intake of Red Meat in dinner",
    "Limit intake of sprouted lentils in dinner",
    "Limit intake of Starchy vegetables before sleep",
    "Limit intake of Green Leafy Vegetables before sleep",
    "Limit intake of Cruciferous Vegetables before sleep",
    "Limit intake of Roots & Tubers before sleep",
    "Limit intake of Goitrogen before sleep",
    "Limit intake of Red Meat before sleep",
    "Limit intake of Lean Meat before sleep",
    "Limit intake of Split Lentils before sleep",
    "Limit intake of sprouted lentils before sleep",
    "Limit intake of Goitrogen post-workout",
    "Limit intake of Red Meat post-workout",
    "Limit intake of Red Meat pre-workout",
    "Stop intake of Starchy vegetables In Diet",
    "Stop intake of Cruciferous Vegetables In Diet",
    "Stop intake of Roots & Tubers In Diet",
    "Stop intake of Goitrogen In Diet",
    "Stop intake of Red Meat In Diet",
    "Stop intake of Split Lentils In Diet",
    "Stop intake of Citrus Fruits In Diet",
    "Stop intake of Millets In Diet",
    "Stop intake of Goitrogen post waking up",
    "Stop intake of Red Meat post waking up",
    "Stop intake of Goitrogen in breakfast",
    "Stop intake of Red Meat in breakfast",
    "Stop intake of sprouted lentils in breakfast",
    "Stop intake of Goitrogen in pre-lunch snack",
    "Stop intake of Red Meat in pre-lunch snack",
    "Stop intake of Goitrogen in lunch",
    "Stop intake of Red Meat in lunch",
    "Stop intake of Goitrogen in snacks",
    "Stop intake of Red Meat in snacks",
    "Stop intake of Goitrogen in dinner",
    "Stop intake of Red Meat in dinner",
    "Stop intake of Goitrogen before sleep",
    "Stop intake of Red Meat before sleep",
    "Stop intake of Goitrogen post-workout",
    "Stop intake of Red Meat post-workout",
    "Stop intake of Goitrogen pre-workout",
    "Stop intake of Red Meat pre-workout",
    "Increase intake of Fish In Diet",
    "Increase intake of Chicken in lunch",
    "Increase intake of Egg in lunch",
    "Increase intake of Egg Yolk in lunch",
    "Increase intake of Salmon in lunch",
    "Increase intake of Tuna in lunch",
    "Increase intake of Fish in lunch",
    "Increase intake of Chicken post-workout",
    "Increase intake of Egg post-workout",
    "Limit intake of Chicken In Diet",
    "Limit intake of Egg In Diet",
    "Limit intake of Egg Yolk In Diet",
    "Limit intake of Salmon In Diet",
    "Limit intake of Tuna In Diet",
    "Limit intake of Fish In Diet",
    "Limit intake of Chicken post waking up",
    "Limit intake of Egg post waking up",
    "Limit intake of Egg Yolk post waking up",
    "Limit intake of Salmon post waking up",
    "Limit intake of Tuna post waking up",
    "Limit intake of Fish post waking up",
    "Limit intake of Chicken in breakfast",
    "Limit intake of Egg in breakfast",
    "Limit intake of Egg Yolk in breakfast",
    "Limit intake of Salmon in breakfast",
    "Limit intake of Tuna in breakfast",
    "Limit intake of Fish in breakfast",
    "Limit intake of Chicken in pre-lunch snack",
    "Limit intake of Egg in pre-lunch snack",
    "Limit intake of Egg Yolk in pre-lunch snack",
    "Limit intake of Salmon in pre-lunch snack",
    "Limit intake of Tuna in pre-lunch snack",
    "Limit intake of Fish in pre-lunch snack",
    "Limit intake of Chicken in lunch",
    "Limit intake of Egg in lunch",
    "Limit intake of Egg Yolk in lunch",
    "Limit intake of Salmon in lunch",
    "Limit intake of Tuna in lunch",
    "Limit intake of Fish in lunch",
    "Limit intake of Chicken in snacks",
    "Limit intake of Egg in snacks",
    "Limit intake of Egg Yolk in snacks",
    "Limit intake of Salmon in snacks",
    "Limit intake of Tuna in snacks",
    "Limit intake of Fish in snacks",
    "Limit intake of Chicken in dinner",
    "Limit intake of Egg in dinner",
    "Limit intake of Egg Yolk in dinner",
    "Limit intake of Salmon in dinner",
    "Limit intake of Tuna in dinner",
    "Limit intake of Fish in dinner",
    "Limit intake of Chicken before sleep",
    "Limit intake of Egg before sleep",
    "Limit intake of Egg Yolk before sleep",
    "Limit intake of Salmon before sleep",
    "Limit intake of Tuna before sleep",
    "Limit intake of Fish before sleep",
    "Limit intake of Chicken post-workout",
    "Limit intake of Egg post-workout",
    "Limit intake of Egg Yolk post-workout",
    "Limit intake of Salmon post-workout",
    "Limit intake of Tuna post-workout",
    "Limit intake of Fish post-workout",
    "Limit intake of Chicken pre-workout",
    "Limit intake of Egg pre-workout",
    "Limit intake of Egg Yolk pre-workout",
    "Limit intake of Salmon pre-workout",
    "Limit intake of Tuna pre-workout",
    "Limit intake of Fish pre-workout",
    "Stop intake of Chicken In Diet",
    "Stop intake of Egg In Diet",
    "Stop intake of Egg Yolk In Diet",
    "Stop intake of Salmon In Diet",
    "Stop intake of Tuna In Diet",
    "Stop intake of Fish In Diet",
    "Stop intake of Chicken post waking up",
    "Stop intake of Egg post waking up",
    "Stop intake of Egg Yolk post waking up",
    "Stop intake of Salmon post waking up",
    "Stop intake of Tuna post waking up",
    "Stop intake of Fish post waking up",
    "Stop intake of Chicken in breakfast",
    "Stop intake of Egg in breakfast",
    "Stop intake of Egg Yolk in breakfast",
    "Stop intake of Salmon in breakfast",
    "Stop intake of Tuna in breakfast",
    "Stop intake of Fish in breakfast",
    "Stop intake of Chicken in pre-lunch snack",
    "Stop intake of Egg in pre-lunch snack",
    "Stop intake of Egg Yolk in pre-lunch snack",
    "Stop intake of Salmon in pre-lunch snack",
    "Stop intake of Tuna in pre-lunch snack",
    "Stop intake of Fish in pre-lunch snack",
    "Stop intake of Chicken in lunch",
    "Stop intake of Egg in lunch",
    "Stop intake of Egg Yolk in lunch",
    "Stop intake of Salmon in lunch",
    "Stop intake of Tuna in lunch",
    "Stop intake of Fish in lunch",
    "Stop intake of Chicken in snacks",
    "Stop intake of Egg in snacks",
    "Stop intake of Egg Yolk in snacks",
    "Stop intake of Salmon in snacks",
    "Stop intake of Tuna in snacks",
    "Stop intake of Fish in snacks",
    "Stop intake of Chicken in dinner",
    "Stop intake of Egg in dinner",
    "Stop intake of Egg Yolk in dinner",
    "Stop intake of Salmon in dinner",
    "Stop intake of Tuna in dinner",
    "Stop intake of Fish in dinner",
    "Stop intake of Chicken before sleep",
    "Stop intake of Egg before sleep",
    "Stop intake of Egg Yolk before sleep",
    "Stop intake of Salmon before sleep",
    "Stop intake of Tuna before sleep",
    "Stop intake of Fish before sleep",
    "Stop intake of Chicken post-workout",
    "Stop intake of Egg post-workout",
    "Stop intake of Egg Yolk post-workout",
    "Stop intake of Salmon post-workout",
    "Stop intake of Tuna post-workout",
    "Stop intake of Fish post-workout",
    "Stop intake of Chicken pre-workout",
    "Stop intake of Egg pre-workout",
    "Stop intake of Egg Yolk pre-workout",
    "Stop intake of Salmon pre-workout",
    "Stop intake of Tuna pre-workout",
    "Stop intake of Fish pre-workout",
    "Increase intake of Peas In Diet",
    "Increase intake of Channa post-workout",
    "Increase intake of Rajma post-workout",
    "Increase intake of Soyabean post-workout",
    "Increase intake of Peas post-workout",
    "Limit intake of Channa In Diet",
    "Limit intake of Rajma In Diet",
    "Limit intake of Soyabean In Diet",
    "Limit intake of Channa post waking up",
    "Limit intake of Rajma post waking up",
    "Limit intake of Soyabean post waking up",
    "Limit intake of Peas post waking up",
    "Limit intake of Channa in breakfast",
    "Limit intake of Rajma in breakfast",
    "Limit intake of Soyabean in breakfast",
    "Limit intake of Peas in breakfast",
    "Limit intake of Channa in pre-lunch snack",
    "Limit intake of Rajma in pre-lunch snack",
    "Limit intake of Soyabean in pre-lunch snack",
    "Limit intake of Peas in pre-lunch snack",
    "Limit intake of Channa in lunch",
    "Limit intake of Rajma in lunch",
    "Limit intake of Soyabean in lunch",
    "Limit intake of Peas in lunch",
    "Limit intake of Channa in snacks",
    "Limit intake of Rajma in snacks",
    "Limit intake of Soyabean in snacks",
    "Limit intake of Peas in snacks",
    "Limit intake of Channa in dinner",
    "Limit intake of Rajma in dinner",
    "Limit intake of Soyabean in dinner",
    "Limit intake of Peas in dinner",
    "Limit intake of Channa before sleep",
    "Limit intake of Rajma before sleep",
    "Limit intake of Soyabean before sleep",
    "Limit intake of Peas before sleep",
    "Limit intake of Channa post-workout",
    "Limit intake of Rajma post-workout",
    "Limit intake of Soyabean post-workout",
    "Limit intake of Peas post-workout",
    "Limit intake of Channa pre-workout",
    "Limit intake of Rajma pre-workout",
    "Limit intake of Soyabean pre-workout",
    "Limit intake of Peas pre-workout",
    "Stop intake of Channa In Diet",
    "Stop intake of Rajma In Diet",
    "Stop intake of Soyabean In Diet",
    "Stop intake of Channa post waking up",
    "Stop intake of Rajma post waking up",
    "Stop intake of Soyabean post waking up",
    "Stop intake of Peas post waking up",
    "Stop intake of Channa in breakfast",
    "Stop intake of Rajma in breakfast",
    "Stop intake of Soyabean in breakfast",
    "Stop intake of Peas in breakfast",
    "Stop intake of Channa in pre-lunch snack",
    "Stop intake of Rajma in pre-lunch snack",
    "Stop intake of Soyabean in pre-lunch snack",
    "Stop intake of Channa in lunch",
    "Stop intake of Rajma in lunch",
    "Stop intake of Soyabean in lunch",
    "Stop intake of Channa in snacks",
    "Stop intake of Rajma in snacks",
    "Stop intake of Soyabean in snacks",
    "Stop intake of Channa in dinner",
    "Stop intake of Rajma in dinner",
    "Stop intake of Soyabean in dinner",
    "Stop intake of Channa before sleep",
    "Stop intake of Rajma before sleep",
    "Stop intake of Soyabean before sleep",
    "Stop intake of Channa post-workout",
    "Stop intake of Rajma post-workout",
    "Stop intake of Soyabean post-workout",
    "Stop intake of Channa pre-workout",
    "Stop intake of Rajma pre-workout",
    "Stop intake of Soyabean pre-workout",
    "Increase intake of Milk In Diet",
    "Increase intake of Curd In Diet",
    "Increase intake of Yogurt In Diet",
    "Increase intake of Skimmed Milk In Diet",
    "Increase intake of Fat Free Yogurt In Diet",
    "Increase intake of Milk in breakfast",
    "Increase intake of Curd in breakfast",
    "Increase intake of Yogurt in breakfast",
    "Increase intake of Skimmed Milk in breakfast",
    "Increase intake of Fat Free Yogurt in breakfast",
    "Increase intake of Curd in lunch",
    "Increase intake of Yogurt in lunch",
    "Increase intake of Fat Free Yogurt in lunch",
    "Increase intake of Butter Milk in lunch",
    "Increase intake of Curd in dinner",
    "Increase intake of Yogurt in dinner",
    "Increase intake of Fat Free Yogurt in dinner",
    "Increase intake of Butter Milk in dinner",
    "Limit intake of Milk In Diet",
    "Limit intake of Curd In Diet",
    "Limit intake of Yogurt In Diet",
    "Limit intake of Skimmed Milk In Diet",
    "Limit intake of Fat Free Yogurt In Diet",
    "Limit intake of Cheese In Diet",
    "Limit intake of Paneer In Diet",
    "Limit intake of Butter In Diet",
    "Limit intake of Ghee In Diet",
    "Limit intake of Butter Milk In Diet",
    "Limit intake of Milk post waking up",
    "Limit intake of Curd post waking up",
    "Limit intake of Yogurt post waking up",
    "Limit intake of Skimmed Milk post waking up",
    "Limit intake of Fat Free Yogurt post waking up",
    "Limit intake of Cheese post waking up",
    "Limit intake of Paneer post waking up",
    "Limit intake of Butter post waking up",
    "Limit intake of Ghee post waking up",
    "Limit intake of Butter Milk post waking up",
    "Limit intake of Milk in breakfast",
    "Limit intake of Curd in breakfast",
    "Limit intake of Yogurt in breakfast",
    "Limit intake of Skimmed Milk in breakfast",
    "Limit intake of Fat Free Yogurt in breakfast",
    "Limit intake of Cheese in breakfast",
    "Limit intake of Paneer in breakfast",
    "Limit intake of Butter in breakfast",
    "Limit intake of Ghee in breakfast",
    "Limit intake of Butter Milk in breakfast",
    "Limit intake of Milk in pre-lunch snack",
    "Limit intake of Curd in pre-lunch snack",
    "Limit intake of Yogurt in pre-lunch snack",
    "Limit intake of Skimmed Milk in pre-lunch snack",
    "Limit intake of Fat Free Yogurt in pre-lunch snack",
    "Limit intake of Cheese in pre-lunch snack",
    "Limit intake of Paneer in pre-lunch snack",
    "Limit intake of Butter in pre-lunch snack",
    "Limit intake of Ghee in pre-lunch snack",
    "Limit intake of Butter Milk in pre-lunch snack",
    "Limit intake of Milk in lunch",
    "Limit intake of Curd in lunch",
    "Limit intake of Yogurt in lunch",
    "Limit intake of Skimmed Milk in lunch",
    "Limit intake of Fat Free Yogurt in lunch",
    "Limit intake of Cheese in lunch",
    "Limit intake of Paneer in lunch",
    "Limit intake of Butter in lunch",
    "Limit intake of Ghee in lunch",
    "Limit intake of Butter Milk in lunch",
    "Limit intake of Milk in snacks",
    "Limit intake of Curd in snacks",
    "Limit intake of Yogurt in snacks",
    "Limit intake of Skimmed Milk in snacks",
    "Limit intake of Fat Free Yogurt in snacks",
    "Limit intake of Cheese in snacks",
    "Limit intake of Paneer in snacks",
    "Limit intake of Butter in snacks",
    "Limit intake of Ghee in snacks",
    "Limit intake of Butter Milk in snacks",
    "Limit intake of Milk in dinner",
    "Limit intake of Curd in dinner",
    "Limit intake of Yogurt in dinner",
    "Limit intake of Skimmed Milk in dinner",
    "Limit intake of Fat Free Yogurt in dinner",
    "Limit intake of Cheese in dinner",
    "Limit intake of Paneer in dinner",
    "Limit intake of Butter in dinner",
    "Limit intake of Ghee in dinner",
    "Limit intake of Butter Milk in dinner",
    "Limit intake of Milk before sleep",
    "Limit intake of Curd before sleep",
    "Limit intake of Yogurt before sleep",
    "Limit intake of Skimmed Milk before sleep",
    "Limit intake of Fat Free Yogurt before sleep",
    "Limit intake of Cheese before sleep",
    "Limit intake of Paneer before sleep",
    "Limit intake of Butter before sleep",
    "Limit intake of Ghee before sleep",
    "Limit intake of Butter Milk before sleep",
    "Limit intake of Milk post-workout",
    "Limit intake of Curd post-workout",
    "Limit intake of Yogurt post-workout",
    "Limit intake of Skimmed Milk post-workout",
    "Limit intake of Fat Free Yogurt post-workout",
    "Limit intake of Cheese post-workout",
    "Limit intake of Paneer post-workout",
    "Limit intake of Butter post-workout",
    "Limit intake of Ghee post-workout",
    "Limit intake of Butter Milk post-workout",
    "Limit intake of Milk pre-workout",
    "Limit intake of Curd pre-workout",
    "Limit intake of Yogurt pre-workout",
    "Limit intake of Skimmed Milk pre-workout",
    "Limit intake of Fat Free Yogurt pre-workout",
    "Limit intake of Cheese pre-workout",
    "Limit intake of Paneer pre-workout",
    "Limit intake of Butter pre-workout",
    "Limit intake of Ghee pre-workout",
    "Limit intake of Butter Milk pre-workout",
    "Stop intake of Milk In Diet",
    "Stop intake of Curd In Diet",
    "Stop intake of Yogurt In Diet",
    "Stop intake of Skimmed Milk In Diet",
    "Stop intake of Fat Free Yogurt In Diet",
    "Stop intake of Cheese In Diet",
    "Stop intake of Paneer In Diet",
    "Stop intake of Butter In Diet",
    "Stop intake of Ghee In Diet",
    "Stop intake of Butter Milk In Diet",
    "Stop intake of Milk post waking up",
    "Stop intake of Curd post waking up",
    "Stop intake of Yogurt post waking up",
    "Stop intake of Skimmed Milk post waking up",
    "Stop intake of Fat Free Yogurt post waking up",
    "Stop intake of Cheese post waking up",
    "Stop intake of Paneer post waking up",
    "Stop intake of Butter post waking up",
    "Stop intake of Ghee post waking up",
    "Stop intake of Butter Milk post waking up",
    "Stop intake of Milk in breakfast",
    "Stop intake of Curd in breakfast",
    "Stop intake of Yogurt in breakfast",
    "Stop intake of Skimmed Milk in breakfast",
    "Stop intake of Fat Free Yogurt in breakfast",
    "Stop intake of Cheese in breakfast",
    "Stop intake of Paneer in breakfast",
    "Stop intake of Butter in breakfast",
    "Stop intake of Ghee in breakfast",
    "Stop intake of Butter Milk in breakfast",
    "Stop intake of Milk in pre-lunch snack",
    "Stop intake of Curd in pre-lunch snack",
    "Stop intake of Yogurt in pre-lunch snack",
    "Stop intake of Skimmed Milk in pre-lunch snack",
    "Stop intake of Fat Free Yogurt in pre-lunch snack",
    "Stop intake of Cheese in pre-lunch snack",
    "Stop intake of Paneer in pre-lunch snack",
    "Stop intake of Butter in pre-lunch snack",
    "Stop intake of Ghee in pre-lunch snack",
    "Stop intake of Butter Milk in pre-lunch snack",
    "Stop intake of Milk in lunch",
    "Stop intake of Curd in lunch",
    "Stop intake of Yogurt in lunch",
    "Stop intake of Skimmed Milk in lunch",
    "Stop intake of Fat Free Yogurt in lunch",
    "Stop intake of Cheese in lunch",
    "Stop intake of Paneer in lunch",
    "Stop intake of Butter in lunch",
    "Stop intake of Ghee in lunch",
    "Stop intake of Butter Milk in lunch",
    "Stop intake of Milk in snacks",
    "Stop intake of Curd in snacks",
    "Stop intake of Yogurt in snacks",
    "Stop intake of Skimmed Milk in snacks",
    "Stop intake of Fat Free Yogurt in snacks",
    "Stop intake of Cheese in snacks",
    "Stop intake of Paneer in snacks",
    "Stop intake of Butter in snacks",
    "Stop intake of Ghee in snacks",
    "Stop intake of Butter Milk in snacks",
    "Stop intake of Milk in dinner",
    "Stop intake of Curd in dinner",
    "Stop intake of Yogurt in dinner",
    "Stop intake of Skimmed Milk in dinner",
    "Stop intake of Fat Free Yogurt in dinner",
    "Stop intake of Cheese in dinner",
    "Stop intake of Paneer in dinner",
    "Stop intake of Butter in dinner",
    "Stop intake of Ghee in dinner",
    "Stop intake of Butter Milk in dinner",
    "Stop intake of Milk before sleep",
    "Stop intake of Curd before sleep",
    "Stop intake of Yogurt before sleep",
    "Stop intake of Skimmed Milk before sleep",
    "Stop intake of Fat Free Yogurt before sleep",
    "Stop intake of Cheese before sleep",
    "Stop intake of Paneer before sleep",
    "Stop intake of Butter before sleep",
    "Stop intake of Ghee before sleep",
    "Stop intake of Butter Milk before sleep",
    "Stop intake of Milk post-workout",
    "Stop intake of Curd post-workout",
    "Stop intake of Yogurt post-workout",
    "Stop intake of Skimmed Milk post-workout",
    "Stop intake of Fat Free Yogurt post-workout",
    "Stop intake of Cheese post-workout",
    "Stop intake of Paneer post-workout",
    "Stop intake of Butter post-workout",
    "Stop intake of Ghee post-workout",
    "Stop intake of Butter Milk post-workout",
    "Stop intake of Milk pre-workout",
    "Stop intake of Curd pre-workout",
    "Stop intake of Yogurt pre-workout",
    "Stop intake of Skimmed Milk pre-workout",
    "Stop intake of Fat Free Yogurt pre-workout",
    "Stop intake of Cheese pre-workout",
    "Stop intake of Paneer pre-workout",
    "Stop intake of Butter pre-workout",
    "Stop intake of Ghee pre-workout",
    "Stop intake of Butter Milk pre-workout",
    "Increase intake of Millet In Diet",
    "Increase intake of Rice In Diet",
    "Limit intake of Wheat In Diet",
    "Limit intake of Oats In Diet",
    "Limit intake of Millet In Diet",
    "Limit intake of Rice In Diet",
    "Limit intake of Amaranth In Diet",
    "Limit intake of BuckWheat In Diet",
    "Limit intake of Quinoa In Diet",
    "Limit intake of Wheat post waking up",
    "Limit intake of Oats post waking up",
    "Limit intake of Millet post waking up",
    "Limit intake of Rice post waking up",
    "Limit intake of Amaranth post waking up",
    "Limit intake of BuckWheat post waking up",
    "Limit intake of Quinoa post waking up",
    "Limit intake of Wheat in breakfast",
    "Limit intake of Oats in breakfast",
    "Limit intake of Millet in breakfast",
    "Limit intake of Rice in breakfast",
    "Limit intake of Amaranth in breakfast",
    "Limit intake of BuckWheat in breakfast",
    "Limit intake of Quinoa in breakfast",
    "Limit intake of Wheat in pre-lunch snack",
    "Limit intake of Oats in pre-lunch snack",
    "Limit intake of Millet in pre-lunch snack",
    "Limit intake of Rice in pre-lunch snack",
    "Limit intake of Amaranth in pre-lunch snack",
    "Limit intake of BuckWheat in pre-lunch snack",
    "Limit intake of Quinoa in pre-lunch snack",
    "Limit intake of Wheat in lunch",
    "Limit intake of Oats in lunch",
    "Limit intake of Millet in lunch",
    "Limit intake of Rice in lunch",
    "Limit intake of Amaranth in lunch",
    "Limit intake of BuckWheat in lunch",
    "Limit intake of Quinoa in lunch",
    "Limit intake of Wheat in snacks",
    "Limit intake of Oats in snacks",
    "Limit intake of Millet in snacks",
    "Limit intake of Rice in snacks",
    "Limit intake of Amaranth in snacks",
    "Limit intake of BuckWheat in snacks",
    "Limit intake of Quinoa in snacks",
    "Limit intake of Wheat in dinner",
    "Limit intake of Oats in dinner",
    "Limit intake of Millet in dinner",
    "Limit intake of Rice in dinner",
    "Limit intake of Amaranth in dinner",
    "Limit intake of BuckWheat in dinner",
    "Limit intake of Quinoa in dinner",
    "Limit intake of Wheat before sleep",
    "Limit intake of Oats before sleep",
    "Limit intake of Millet before sleep",
    "Limit intake of Rice before sleep",
    "Limit intake of Amaranth before sleep",
    "Limit intake of BuckWheat before sleep",
    "Limit intake of Quinoa before sleep",
    "Limit intake of Wheat post-workout",
    "Limit intake of Oats post-workout",
    "Limit intake of Millet post-workout",
    "Limit intake of Rice post-workout",
    "Limit intake of Amaranth post-workout",
    "Limit intake of BuckWheat post-workout",
    "Limit intake of Quinoa post-workout",
    "Limit intake of Wheat pre-workout",
    "Limit intake of Oats pre-workout",
    "Limit intake of Millet pre-workout",
    "Limit intake of Rice pre-workout",
    "Limit intake of Amaranth pre-workout",
    "Limit intake of BuckWheat pre-workout",
    "Limit intake of Quinoa pre-workout",
    "Stop intake of Wheat In Diet",
    "Stop intake of Oats In Diet",
    "Stop intake of Millet In Diet",
    "Stop intake of Rice In Diet",
    "Stop intake of Amaranth In Diet",
    "Stop intake of BuckWheat In Diet",
    "Stop intake of Quinoa In Diet",
    "Stop intake of Wheat post waking up",
    "Stop intake of Oats post waking up",
    "Stop intake of Millet post waking up",
    "Stop intake of Rice post waking up",
    "Stop intake of Amaranth post waking up",
    "Stop intake of BuckWheat post waking up",
    "Stop intake of Quinoa post waking up",
    "Stop intake of Wheat in breakfast",
    "Stop intake of Oats in breakfast",
    "Stop intake of Millet in breakfast",
    "Stop intake of Rice in breakfast",
    "Stop intake of Amaranth in breakfast",
    "Stop intake of BuckWheat in breakfast",
    "Stop intake of Quinoa in breakfast",
    "Stop intake of Wheat in pre-lunch snack",
    "Stop intake of Oats in pre-lunch snack",
    "Stop intake of Millet in pre-lunch snack",
    "Stop intake of Rice in pre-lunch snack",
    "Stop intake of Amaranth in pre-lunch snack",
    "Stop intake of BuckWheat in pre-lunch snack",
    "Stop intake of Quinoa in pre-lunch snack",
    "Stop intake of Wheat in lunch",
    "Stop intake of Oats in lunch",
    "Stop intake of Millet in lunch",
    "Stop intake of Rice in lunch",
    "Stop intake of Amaranth in lunch",
    "Stop intake of BuckWheat in lunch",
    "Stop intake of Quinoa in lunch",
    "Stop intake of Wheat in snacks",
    "Stop intake of Oats in snacks",
    "Stop intake of Millet in snacks",
    "Stop intake of Rice in snacks",
    "Stop intake of Amaranth in snacks",
    "Stop intake of BuckWheat in snacks",
    "Stop intake of Quinoa in snacks",
    "Stop intake of Wheat in dinner",
    "Stop intake of Oats in dinner",
    "Stop intake of Millet in dinner",
    "Stop intake of Rice in dinner",
    "Stop intake of Amaranth in dinner",
    "Stop intake of BuckWheat in dinner",
    "Stop intake of Quinoa in dinner",
    "Stop intake of Wheat before sleep",
    "Stop intake of Oats before sleep",
    "Stop intake of Millet before sleep",
    "Stop intake of Rice before sleep",
    "Stop intake of Amaranth before sleep",
    "Stop intake of BuckWheat before sleep",
    "Stop intake of Quinoa before sleep",
    "Stop intake of Wheat post-workout",
    "Stop intake of Oats post-workout",
    "Stop intake of Millet post-workout",
    "Stop intake of Rice post-workout",
    "Stop intake of Amaranth post-workout",
    "Stop intake of BuckWheat post-workout",
    "Stop intake of Quinoa post-workout",
    "Stop intake of Wheat pre-workout",
    "Stop intake of Oats pre-workout",
    "Stop intake of Millet pre-workout",
    "Stop intake of Rice pre-workout",
    "Stop intake of Amaranth pre-workout",
    "Stop intake of BuckWheat pre-workout",
    "Stop intake of Quinoa pre-workout",
    "Increase intake of Almonds In Diet",
    "Increase intake of Walnuts In Diet",
    "Increase intake of Pistacchio In Diet",
    "Increase intake of Raisins In Diet",
    "Increase intake of Dates In Diet",
    "Increase intake of Peanuts In Diet",
    "Increase intake of Flaxseed In Diet",
    "Increase intake of Moringa In Diet",
    "Increase intake of Chia seed In Diet",
    "Increase intake of Sesame seeds In Diet",
    "Increase intake of Fenugreek In Diet",
    "Increase intake of Pumpkin Seeds In Diet",
    "Increase intake of Hemp Seeds In Diet",
    "Increase intake of Almonds post-workout",
    "Increase intake of Walnuts post-workout",
    "Increase intake of Pistacchio post-workout",
    "Increase intake of Raisins post-workout",
    "Increase intake of Dates post-workout",
    "Increase intake of Peanuts post-workout",
    "Increase intake of Flaxseed post-workout",
    "Increase intake of Moringa post-workout",
    "Increase intake of Chia seed post-workout",
    "Increase intake of Sesame seeds post-workout",
    "Increase intake of Fenugreek post-workout",
    "Increase intake of Pumpkin Seeds post-workout",
    "Increase intake of Hemp Seeds post-workout",
    "Increase intake of Almonds pre-workout",
    "Increase intake of Walnuts pre-workout",
    "Increase intake of Pistacchio pre-workout",
    "Increase intake of Raisins pre-workout",
    "Increase intake of Dates pre-workout",
    "Increase intake of Peanuts pre-workout",
    "Increase intake of Flaxseed pre-workout",
    "Increase intake of Moringa pre-workout",
    "Increase intake of Chia seed pre-workout",
    "Increase intake of Sesame seeds pre-workout",
    "Increase intake of Fenugreek pre-workout",
    "Increase intake of Pumpkin Seeds pre-workout",
    "Increase intake of Hemp Seeds pre-workout",
    "Limit intake of Almonds In Diet",
    "Limit intake of Walnuts In Diet",
    "Limit intake of Pistacchio In Diet",
    "Limit intake of Raisins In Diet",
    "Limit intake of Dates In Diet",
    "Limit intake of Peanuts In Diet",
    "Limit intake of Flaxseed In Diet",
    "Limit intake of Moringa In Diet",
    "Limit intake of Chia seed In Diet",
    "Limit intake of Sesame seeds In Diet",
    "Limit intake of Fenugreek In Diet",
    "Limit intake of Pumpkin Seeds In Diet",
    "Limit intake of Hemp Seeds In Diet",
    "Limit intake of Almonds post waking up",
    "Limit intake of Walnuts post waking up",
    "Limit intake of Pistacchio post waking up",
    "Limit intake of Raisins post waking up",
    "Limit intake of Dates post waking up",
    "Limit intake of Peanuts post waking up",
    "Limit intake of Flaxseed post waking up",
    "Limit intake of Moringa post waking up",
    "Limit intake of Chia seed post waking up",
    "Limit intake of Sesame seeds post waking up",
    "Limit intake of Fenugreek post waking up",
    "Limit intake of Pumpkin Seeds post waking up",
    "Limit intake of Hemp Seeds post waking up",
    "Limit intake of Almonds in breakfast",
    "Limit intake of Walnuts in breakfast",
    "Limit intake of Pistacchio in breakfast",
    "Limit intake of Raisins in breakfast",
    "Limit intake of Dates in breakfast",
    "Limit intake of Peanuts in breakfast",
    "Limit intake of Flaxseed in breakfast",
    "Limit intake of Moringa in breakfast",
    "Limit intake of Chia seed in breakfast",
    "Limit intake of Sesame seeds in breakfast",
    "Limit intake of Fenugreek in breakfast",
    "Limit intake of Pumpkin Seeds in breakfast",
    "Limit intake of Hemp Seeds in breakfast",
    "Limit intake of Almonds in pre-lunch snack",
    "Limit intake of Walnuts in pre-lunch snack",
    "Limit intake of Pistacchio in pre-lunch snack",
    "Limit intake of Raisins in pre-lunch snack",
    "Limit intake of Dates in pre-lunch snack",
    "Limit intake of Peanuts in pre-lunch snack",
    "Limit intake of Flaxseed in pre-lunch snack",
    "Limit intake of Moringa in pre-lunch snack",
    "Limit intake of Chia seed in pre-lunch snack",
    "Limit intake of Sesame seeds in pre-lunch snack",
    "Limit intake of Fenugreek in pre-lunch snack",
    "Limit intake of Pumpkin Seeds in pre-lunch snack",
    "Limit intake of Hemp Seeds in pre-lunch snack",
    "Limit intake of Almonds in lunch",
    "Limit intake of Walnuts in lunch",
    "Limit intake of Pistacchio in lunch",
    "Limit intake of Raisins in lunch",
    "Limit intake of Dates in lunch",
    "Limit intake of Peanuts in lunch",
    "Limit intake of Flaxseed in lunch",
    "Limit intake of Moringa in lunch",
    "Limit intake of Chia seed in lunch",
    "Limit intake of Sesame seeds in lunch",
    "Limit intake of Fenugreek in lunch",
    "Limit intake of Pumpkin Seeds in lunch",
    "Limit intake of Hemp Seeds in lunch",
    "Limit intake of Almonds in snacks",
    "Limit intake of Walnuts in snacks",
    "Limit intake of Pistacchio in snacks",
    "Limit intake of Raisins in snacks",
    "Limit intake of Dates in snacks",
    "Limit intake of Peanuts in snacks",
    "Limit intake of Flaxseed in snacks",
    "Limit intake of Moringa in snacks",
    "Limit intake of Chia seed in snacks",
    "Limit intake of Sesame seeds in snacks",
    "Limit intake of Fenugreek in snacks",
    "Limit intake of Pumpkin Seeds in snacks",
    "Limit intake of Hemp Seeds in snacks",
    "Limit intake of Almonds in dinner",
    "Limit intake of Walnuts in dinner",
    "Limit intake of Pistacchio in dinner",
    "Limit intake of Raisins in dinner",
    "Limit intake of Dates in dinner",
    "Limit intake of Peanuts in dinner",
    "Limit intake of Flaxseed in dinner",
    "Limit intake of Moringa in dinner",
    "Limit intake of Chia seed in dinner",
    "Limit intake of Sesame seeds in dinner",
    "Limit intake of Fenugreek in dinner",
    "Limit intake of Pumpkin Seeds in dinner",
    "Limit intake of Hemp Seeds in dinner",
    "Limit intake of Almonds before sleep",
    "Limit intake of Walnuts before sleep",
    "Limit intake of Pistacchio before sleep",
    "Limit intake of Raisins before sleep",
    "Limit intake of Dates before sleep",
    "Limit intake of Peanuts before sleep",
    "Limit intake of Flaxseed before sleep",
    "Limit intake of Moringa before sleep",
    "Limit intake of Chia seed before sleep",
    "Limit intake of Sesame seeds before sleep",
    "Limit intake of Fenugreek before sleep",
    "Limit intake of Pumpkin Seeds before sleep",
    "Limit intake of Hemp Seeds before sleep",
    "Limit intake of Almonds post-workout",
    "Limit intake of Walnuts post-workout",
    "Limit intake of Pistacchio post-workout",
    "Limit intake of Raisins post-workout",
    "Limit intake of Dates post-workout",
    "Limit intake of Peanuts post-workout",
    "Limit intake of Flaxseed post-workout",
    "Limit intake of Moringa post-workout",
    "Limit intake of Chia seed post-workout",
    "Limit intake of Sesame seeds post-workout",
    "Limit intake of Fenugreek post-workout",
    "Limit intake of Pumpkin Seeds post-workout",
    "Limit intake of Hemp Seeds post-workout",
    "Limit intake of Almonds pre-workout",
    "Limit intake of Walnuts pre-workout",
    "Limit intake of Pistacchio pre-workout",
    "Limit intake of Raisins pre-workout",
    "Limit intake of Dates pre-workout",
    "Limit intake of Peanuts pre-workout",
    "Limit intake of Flaxseed pre-workout",
    "Limit intake of Moringa pre-workout",
    "Limit intake of Chia seed pre-workout",
    "Limit intake of Sesame seeds pre-workout",
    "Limit intake of Fenugreek pre-workout",
    "Limit intake of Pumpkin Seeds pre-workout",
    "Limit intake of Hemp Seeds pre-workout",
    "Increase intake of Amla In Diet",
    "Increase intake of Wheatgrass In Diet",
    "Increase intake of Spirulina In Diet",
    "Increase intake of Water In Diet",
    "Increase intake of Tender Coconut Water In Diet",
    "Increase intake of Water post waking up",
    "Increase intake of Tender Coconut Water post waking up",
    "Increase intake of Water in breakfast",
    "Increase intake of Water in pre-lunch snack",
    "Increase intake of Water in lunch",
    "Increase intake of Water in snacks",
    "Increase intake of Water in dinner",
    "Increase intake of Water post-workout",
    "Increase intake of Tender Coconut Water post-workout",
    "Increase intake of Water pre-workout",
    "Increase intake of Tender Coconut Water pre-workout",
    "Limit intake of Maida In Diet",
    "Limit intake of Soy Milk In Diet",
    "Limit intake of Almond Milk In Diet",
    "Limit intake of Salt In Diet",
    "Limit intake of Oil In Diet",
    "Limit intake of Refined sugar In Diet",
    "Limit intake of Water In Diet",
    "Limit intake of Tender Coconut Water In Diet",
    "Limit intake of Jaggery In Diet",
    "Limit intake of Honey In Diet",
    "Limit intake of Maida post waking up",
    "Limit intake of Soy Milk post waking up",
    "Limit intake of Almond Milk post waking up",
    "Limit intake of Salt post waking up",
    "Limit intake of Oil post waking up",
    "Limit intake of Amla post waking up",
    "Limit intake of Wheatgrass post waking up",
    "Limit intake of Spirulina post waking up",
    "Limit intake of Refined sugar post waking up",
    "Limit intake of Water post waking up",
    "Limit intake of Tender Coconut Water post waking up",
    "Limit intake of Jaggery post waking up",
    "Limit intake of Honey post waking up",
    "Limit intake of Maida in breakfast",
    "Limit intake of Soy Milk in breakfast",
    "Limit intake of Almond Milk in breakfast",
    "Limit intake of Salt in breakfast",
    "Limit intake of Oil in breakfast",
    "Limit intake of Refined sugar in breakfast",
    "Limit intake of Water in breakfast",
    "Limit intake of Tender Coconut Water in breakfast",
    "Limit intake of Jaggery in breakfast",
    "Limit intake of Honey in breakfast",
    "Limit intake of Maida in pre-lunch snack",
    "Limit intake of Soy Milk in pre-lunch snack",
    "Limit intake of Almond Milk in pre-lunch snack",
    "Limit intake of Salt in pre-lunch snack",
    "Limit intake of Oil in pre-lunch snack",
    "Limit intake of Refined sugar in pre-lunch snack",
    "Limit intake of Water in pre-lunch snack",
    "Limit intake of Tender Coconut Water in pre-lunch snack",
    "Limit intake of Jaggery in pre-lunch snack",
    "Limit intake of Honey in pre-lunch snack",
    "Limit intake of Maida in lunch",
    "Limit intake of Soy Milk in lunch",
    "Limit intake of Almond Milk in lunch",
    "Limit intake of Salt in lunch",
    "Limit intake of Oil in lunch",
    "Limit intake of Amla in lunch",
    "Limit intake of Refined sugar in lunch",
    "Limit intake of Water in lunch",
    "Limit intake of Tender Coconut Water in lunch",
    "Limit intake of Jaggery in lunch",
    "Limit intake of Honey in lunch",
    "Limit intake of Maida in snacks",
    "Limit intake of Soy Milk in snacks",
    "Limit intake of Almond Milk in snacks",
    "Limit intake of Salt in snacks",
    "Limit intake of Oil in snacks",
    "Limit intake of Refined sugar in snacks",
    "Limit intake of Tender Coconut Water in snacks",
    "Limit intake of Jaggery in snacks",
    "Limit intake of Honey in snacks",
    "Limit intake of Maida in dinner",
    "Limit intake of Almond Milk in dinner",
    "Limit intake of Salt in dinner",
    "Limit intake of Oil in dinner",
    "Limit intake of Refined sugar in dinner",
    "Limit intake of Water in dinner",
    "Limit intake of Tender Coconut Water in dinner",
    "Limit intake of Jaggery in dinner",
    "Limit intake of Honey in dinner",
    "Limit intake of Maida before sleep",
    "Limit intake of Soy Milk before sleep",
    "Limit intake of Almond Milk before sleep",
    "Limit intake of Salt before sleep",
    "Limit intake of Oil before sleep",
    "Limit intake of Refined sugar before sleep",
    "Limit intake of Water before sleep",
    "Limit intake of Tender Coconut Water before sleep",
    "Limit intake of Jaggery before sleep",
    "Limit intake of Honey before sleep",
    "Limit intake of Maida post-workout",
    "Limit intake of Salt post-workout",
    "Limit intake of Oil post-workout",
    "Limit intake of Refined sugar post-workout",
    "Limit intake of Tender Coconut Water post-workout",
    "Limit intake of Jaggery post-workout",
    "Limit intake of Honey post-workout",
    "Limit intake of Maida pre-workout",
    "Limit intake of Salt pre-workout",
    "Limit intake of Oil pre-workout",
    "Limit intake of Refined sugar pre-workout",
    "Limit intake of Jaggery pre-workout",
    "Limit intake of Honey pre-workout",
    "Stop intake of Maida In Diet",
    "Stop intake of Refined sugar In Diet",
    "Stop intake of Honey In Diet",
    "Stop intake of Maida post waking up",
    "Stop intake of Salt post waking up",
    "Stop intake of Oil post waking up",
    "Stop intake of Refined sugar post waking up",
    "Stop intake of Jaggery post waking up",
    "Stop intake of Honey post waking up",
    "Stop intake of Maida in breakfast",
    "Stop intake of Refined sugar in breakfast",
    "Stop intake of Honey in breakfast",
    "Stop intake of Maida in pre-lunch snack",
    "Stop intake of Almond Milk in pre-lunch snack",
    "Stop intake of Salt in pre-lunch snack",
    "Stop intake of Oil in pre-lunch snack",
    "Stop intake of Refined sugar in pre-lunch snack",
    "Stop intake of Honey in pre-lunch snack",
    "Stop intake of Maida in lunch",
    "Stop intake of Refined sugar in lunch",
    "Stop intake of Honey in lunch",
    "Stop intake of Maida in snacks",
    "Stop intake of Refined sugar in snacks",
    "Stop intake of Honey in snacks",
    "Stop intake of Maida in dinner",
    "Stop intake of Refined sugar in dinner",
    "Stop intake of Water in dinner",
    "Stop intake of Honey in dinner",
    "Stop intake of Maida before sleep",
    "Stop intake of Salt before sleep",
    "Stop intake of Oil before sleep",
    "Stop intake of Refined sugar before sleep",
    "Stop intake of Jaggery before sleep",
    "Stop intake of Honey before sleep",
    "Stop intake of Maida post-workout",
    "Stop intake of Oil post-workout",
    "Stop intake of Refined sugar post-workout",
    "Stop intake of Jaggery post-workout",
    "Stop intake of Honey post-workout",
    "Stop intake of Maida pre-workout",
    "Stop intake of Oil pre-workout",
    "Stop intake of Refined sugar pre-workout",
    "Avoid Processed (Packaged, Aerated Drinks) in your regular diet",
    "Include more Raw and Natural Food in your meals",
    "Avoid Food with additive, preservatives or added color",
    "Stop Smoking",
    "Restrict Alcohol to 2-3 times a month",
    "Reduce caffeine intake to 2-3 times a day",
    "Have Early Dinner",
    "Have an early Breakfast",
    "Mid-morning, mid-evening Healthy Snack",
    "Avoid deep fried foods",
    "Adopt a plant-based diet, including whole foods.",
    "Reduce intake of meat and dairy",
    "Reduce intake of refined sugar",
    "Cease consumption of processed food",
    "Increase exposure to sun for Vitamin D",
    "Have 6-8 glasses of water/day",
    "Avoid refined sugar and switch to alternatives of jaggery, dates",
    "Include superfoods in your diet",
    "Avoid preservatives, artificial colours & flavours",
    "Avoid hydrogenated oils and trans-fats",
    "Increase consumption of locally grown, seasonal foods",
    "Cease consumption of tobacco",
    "Reduce consumption of alcohol",
    "Cease consumption of alcohol",
    "Reduce consumption of tea, coffee",
    "Increase consumption of fruits and vegetables",
    "Reduce meal portion sizes",
    "Increase food with low glycemic index",
    "Remove gluten from the diet",
    "Increase daily consumption of raw vegetables & fruit",
    "Limit intake of coffee/tea on waking up",
    "Drink water post waking up",
    "Eat fruits/dryfruits when craving occurs for a sweet tooth",
    "Make heathier food choices when eating out during evenings/or carry healthier choices of foods from home with you"
]

export enum AdherenceCategories {
    FOOD = "FOOD",
    FITNESS = "FITNESS",
    MIND = "MIND",
    MEDICINE = "MEDICINE"
}

export const AdherenceCategoryMap = {
    "FOOD": {
        headerColor: "",
        textColor: "#9f76c3",
        displayValue: "Diet",
        activityTypes: {
            EATFIT_MEAL: "EATFIT_MEAL",
            FOOD_CONSUMPTION: "FOOD_CONSUMPTION"
        }
    },
    "FITNESS": {
        headerColor: "",
        textColor: "#61c1ac",
        displayValue: "Fitness",
        activityTypes: {
            CULT_CLASS: "CULT_CLASS",
            DIY_FITNESS: "DIY_FITNESS",
            WORKOUT_ACTIVITY: "WORKOUT_ACTIVITY"
        }
    },
    "MIND": {
        headerColor: "",
        textColor: "#e85dbe",
        displayValue: "Meditation",
        activityTypes: {
            MIND_CLASS: "MIND_CLASS",
            DIY_MEDITATION: "DIY_MEDITATION",
            MIND_WORKOUT_ACTIVITY: "MIND_WORKOUT_ACTIVITY"
        }
    },
    "MEDICINE": {
        headerColor: "",
        textColor: "#3265a0",
        displayValue: "Clinical",
        activityTypes: {
            MEDICINE_INTAKE: "MEDICINE_INTAKE"
        }
    }
}

export const FieldTypes = {
    TYPEAHEAD: "TYPEAHEAD",
    TEMPLATED_LIST: "TEMPLATED_LIST",
    TEMPLATED_LIST_V3: "TEMPLATED_LIST_V3",
    DROPDOWN: "DROPDOWN",
    TOGGLE_BUTTON: "TOGGLE_BUTTON",
    TEXT_FIELD: "TEXT_FIELD",
    QUESTIONNAIRE: "QUESTIONNAIRE",
    PAIN_SCALE: "PAIN_SCALE",
    PAIN_AREA_VIEW: "PAIN_AREA_VIEW",
    TEXTAREA: "TEXTAREA",
    CHECKBOX: "CHECKBOX",
    GOAL_CHART: "GOAL_CHART",
    HORIZONTAL_TABLE: "HORIZONTAL_TABLE",
    DATE_PICKER: "DATE_PICKER",
    SUGGESTION: "SUGGESTION",
    LABEL: "LABEL",
    NUMERIC_LABEL: "NUMERIC_LABEL",
    FREQUENCY: "FREQUENCY",
    NUMERIC_INPUT: "NUMERIC_INPUT",
    SELECTABLE_TOGGLE_LIST: "SELECTABLE_TOGGLE_LIST",
    NEWLINE: "NEWLINE",
    TABLE: "TABLE",
    STATIC_TYPEAHEAD: "STATIC_TYPEAHEAD",
    MULTISELECT_DROPDOWN: "MULTISELECT_DROPDOWN",
    LINK: "LINK",
    BOOLEAN_TOGGLE: "BOOLEAN_TOGGLE",
    WEEKDAY_SELECTOR: "WEEKDAY_SELECTOR",
    ADVANCED_DURATION: "ADVANCED_DURATION",
    SUGGESTION_LIST: "SUGGESTION_LIST",
    LABEL_LIST: "LABEL_LIST",
    SPACING_BAR: "SPACING_BAR",
    CONFIG_SUGGESTION: "CONFIG_SUGGESTION",
    HEADER: "HEADER",
    TOGGLE_SWITCH: "TOGGLE_SWITCH",
    INPUT_WITH_DROPDOWN: "INPUT_WITH_DROPDOWN"
}

export const TabNames = {
    HISTORY: "History",
    PHYSICAL_ASSESSMENT: "PhysicalAssessment",
    VITALS_AND_ALLERGIES: "VitalsAndAllergies",
    EXAMINATIONS: "Examinations",
    CHIEF_COMPLAINTS: "ChiefComplaints",
    PRESCRIPTION: "Prescription",
    MIND_PLAN: "MindPlan",
    PLAN: "Plan"
}

export const DoctorType = {
    MIND_THERAPIST: "MIND_THERAPIST",
    PSYCHIATRIST: "PSYCHIATRIST",
    PHYSIOTHERAPIST: "PHYSIOTHERAPIST",
    EFA: "ANXIETY_THERAPIST",
    AYURVEDA: "AYURVEDA"
}

export enum SubServiceType {
    COUPLE_THERAPIST = "COUPLE_THERAPIST"
}

export const SchemaType = {
    ARRAY: "array",
    OBJECT: "object"
}

export const ModalSize = {
    XTRALARGE: "x-large",
    LARGE: "large",
    XTRASMALL: "x-small",
    SMALL: "small"
}

export const modalTypes = {
    CONFIRMATION_MODAL: "ConfirmationModal",
    PAST_SESSION_MODAL: "PastSessionModal"
}

export const metricIdsToFetchMetrics = [20, 56, 3, 57, 58, 59, 60, 61, 45, 25, 2, 1]

export const OverallAdherenceScoresAggregation = {
    WEEK: "WEEK",
    MONTH: "MONTH"
};

export const labTestCategories = {
    diagnosti_profile: "diagnosti_profile"
}

export const subSectionsList = [
    "pastConditions",
    "familyHistory"
]

export const PrescriptionPdfSection = {
    ALLERGIES: "allergies",
    VITALS: "vitals",
    CHIEF_SYMPTOMS: "chiefSymptoms",
    PUBLIC_NOTES: "publicNotes",
    DIAGNOSIS: "diagnosis",
    GENERAL_EXAMINATIONS: "generalExaminations",
    ENT_EXAMINATIONS: "entExaminations",
    SYSTEMIC_EXAMINATIONS: "systemicExaminations",
    LAB_TESTS: "labTest",
    MEDICINES: "medicines",
    GENERAL_ADVICE: "adviceTemplate",
    LIFESTYLE_ADVICE: "lifestyleAdvice",
    REFERRAL: "referral",
    FOLLOWUP: "followUp",
    PROVISIONAL_DIAGNOSIS: "provisionalDiagnosis",
    GOALS: "physioGoals",
    PROVISIONAL_TREATMENT_PLAN: "provisionalTreatmentPlan",
    POST_SESSION_ADVICE: "postSessionAdvice",
    ACTIVITY_RECOMMENDATION_STRETCHING: "activityRecommendation-stretching",
    ACTIVITY_RECOMMENDATION_STRENGTHENING: "activityRecommendation-strengthening",
    ACTIVITY_RECOMMENDATION_WORKOUT: "activityRecommendation-workout",
    // PHYSIO_LAB_TEST: "prescriptionLabTest",
    ADDITIONAL_ADVICE: "additionalAdvice",
    PHYSIO_REFERRAL: "physioReferral",
    PHYSIO_FOLLOWUP: "physioFollowUp",
    PHYSIO_LAB_TEST: "physioLabTest",
    BODY_PARTS: "bodyPartsCateredTo"
}

export const AppSources: string[] = ["CUREFIT_NEW_WEBSITE", "CUREFIT_WEBSITE", "CUREFIT_APP", "WEBSITE", "APP", "JEEVES"];

export const URLS = { CDN_URL: "https://d1yfs1c2pfwbpr.cloudfront.net" };

export const ORION_IMG_URL = URLS.CDN_URL + "/image/carefit/orion/";
export const ORION_CSS_URL = URLS.CDN_URL + "/css/carefit/orion/";

export const SUB_SECTIONS_CONTEXT_KEY = {
    CHIEF_COMPLAINTS: "chiefComplaints",
    DIAGNOSIS: "diagnosis",
    HEALTH_TARGET: "healthTarget",
    OTHER_GOALS: "otherGoals",
    PATIENT_INTERVENTIONS: "patient_interventions",
    DIET_PLAN: "dietPlan",
    FITNESS_PLAN: "fitnessPlan",
    MINDFULNESS_PLAN: "mindfulnessPlan",
    HABITS: "habits",
    PRESCRIPTION_INSTRUCTIONS: "prescriptionInstructions",
    MEDICINES: "medicines",
    LAB_TESTS: "labTests",
    PRESCRIPTION_MEDICINES: "prescriptionMedicines",
    LIFESTYLE_ADVICE: "lifeStyleAdvices",
    PRESCRIPTION_DIAGNOSIS: "prescriptionDiagnosis",
    BODY_PARTS: "prescriptionBodyPartsCateredTo",
    REASON_FOR_VISIT: "reasonForVisit"
}

export enum FormItemMode {
    EDIT = "EDIT",
    VIEW = "VIEW"
}

export const testWidgets: any = [
    {
        "sectionKey": "Prescription",
        "subSectionKey": "reasonForVisit",
        "subSectionTitle": "Reason For Visit",
        "subSectionDescription": "",
        "showPastHistory": false,
        "showPastHistoryText": null,
        "isDataRequired": false,
        "validationSchema": {
            "$schema": "http://json-schema.org/draft-07/schema#",
            "title": "prescriptionLabTest Schema",
            "type": "object",
            "properties": {}
        },
        "colWidth": null,
        "modalConfigurationJson": null,
        "subSectionType": null,
        "dataToBeSaved": true,
        "data": [
            {
                "questionTitle": "",
                "key": "reasonForVisitOptions",
                "answerList": [
                    {
                        "type": "BOOLEAN_TOGGLE",
                        "key": "reason",
                        "textSize": "",
                        "values": [
                            { "internalValue": "HCU", "displayValue": "Health Checkup" },
                            { "internalValue": "Follow Up", "displayValue": "Follow Up" },
                            { "internalValue": "Routine Checkup", "displayValue": "Routine Checkup" },
                            { "internalValue": "Medical Certificate", "displayValue": "Medical Certificate" },
                            { "internalValue": "Suspected of Covid-19", "displayValue": "Suspected of Covid-19" }
                        ]
                    }
                ]
            }
        ]
    },
    {
        "sectionKey": "Prescription",
        "subSectionKey": "chiefComplaints",
        "subSectionTitle": "Chief Complaints",
        "subSectionDescription": "",
        "showPastHistory": false,
        "showPastHistoryText": null,
        "isDataRequired": false,
        "validationSchema": {
            "$schema": "http://json-schema.org/draft-07/schema#",
            "title": "prescriptionLabTest Schema",
            "type": "object",
            "properties": {}
        },
        "colWidth": null,
        "modalConfigurationJson": null,
        "subSectionType": null,
        "dataToBeSaved": true,
        "data": [
            {
                "questionTitle": "",
                "key": "presentIllness",
                "answerList": [
                    {
                        "type": "TOGGLE_SWITCH",
                        "key": "noPresentingIllness",
                        "textSize": " ",
                        "header": "",
                        "configuration": {
                            "label": "No Presenting Illness?",
                            "default": false,
                            "action": "clear", // [clear | fill]
                            "linkedFieldKeys": [{
                                "key": "patientComplaints",
                                "value": []
                            }]
                        }
                    },
                ]
            },
            {
                "questionTitle": "",
                "key": "patientComplaints",
                "answerList": [
                    {
                        "type": "TEMPLATED_LIST_V2",
                        "openMode": true,
                        "dependentQues": {
                            "questionKey": "presentIllness",
                            "answerKey": "noPresentingIllness"
                        },
                        "fields": [
                            {
                                "type": "HEADER",
                                "key": "header",
                                "displayAdditionalInfo": false,
                                "textSize": "small",
                                "header": " ",
                                "targetKey": "complaint",
                            },
                            {
                                "type": "ADVANCED_DURATION",
                                "key": "duration",
                                "header": "Duration",
                                "textSize": "large",
                                "toggleValues": [
                                    {
                                        "internalValue": "DAY",
                                        "displayValue": "Day",
                                        "suggestByDefault": true
                                    },
                                    {
                                        "internalValue": "WEEK",
                                        "displayValue": "Week",
                                        "suggestByDefault": true
                                    },
                                    {
                                        "internalValue": "MONTH",
                                        "displayValue": "Month",
                                        "suggestByDefault": true
                                    },
                                    {
                                        "internalValue": "YEAR",
                                        "displayValue": "Year",
                                        "suggestByDefault": true
                                    }
                                ]
                            }
                        ],
                        "defaultItemFields": [
                            {
                                "key": "suggestionsLabel",
                                "type": "LABEL",
                                "labelValue": "Commonly used",
                                "textSize": "xxxsmall"
                            },
                            {
                                "type": "NEWLINE",
                                "key": "emptyRow",
                                "header": "",
                                "inline": false
                            },
                            {
                                "key": "suggestions",
                                "type": "SUGGESTION_LIST",
                                "textSize": "small",
                                "isSuggestion": true,
                                "recommendationKey": "chiefComplaints",
                                "parentFieldKey": "disease",
                                "targetFields": [
                                    {
                                        "key": "complaint",
                                        "primaryTarget": true,
                                        "recommendationValue": "diseaseName",
                                        "targetValueIsObject": true
                                    }

                                ]
                            },
                            {
                                "key": "complaint",
                                "type": "TYPEAHEAD",
                                "typeAheadConfiguration": {
                                    "source": "MAVERICK",
                                    "url": "/maverick/care/symptom_catalogue",
                                    "appendQueryString": true,
                                    "otherKeysMapping": {
                                        "result-size": 5
                                    },
                                    "additionalInfoLabelKeys": [],
                                    "valueKey": "displayName",
                                    "labelKey": "displayName",
                                    "internalKey": "id",
                                    "inputParam": "q",
                                    "allowAddItem": true,
                                    "addItemToTypeahead": {
                                        "displayText": "Click to add new"
                                    },
                                    "resultMapper": {
                                        "internalValue": "internalValue",
                                        "displayValue": "displayValue"
                                    }
                                },
                                "placeholder": "Search",
                                "header": "Complaints",
                                "inline": false,
                                "enableSearchIcon": true
                            },
                        ]
                    }
                ]
            },
            {
                "questionTitle": "",
                "key": "doctorPublicNotes",
                "answerList": [
                    {
                        "key": "publicNotes",
                        "type": "NOTES",
                        "placeholder": "Type",
                        "textSize": "",
                        "label": "Public Notes",
                        "secondaryLabel": "(To be printed on Prescription)"
                    }
                ]
            },
            {
                "questionTitle": "",
                "key": "doctorPrivateNotes",
                "answerList": [
                    {
                        "key": "privateNotes",
                        "type": "NOTES",
                        "placeholder": "Type",
                        "textSize": "",
                        "label": "Private Notes",
                        "secondaryLabel": ""
                    }
                ]
            }
        ]
    },
    {
        "sectionKey": "Prescription",
        "subSectionKey": "prescriptionTemplateMapping",
        "subSectionTitle": "Previous Prescriptions",
        "showPastHistory": false,
        "showPastHistoryText": null,
        "isDataRequired": false,
        "colWidth": null,
        "ViewModalConfiguration": null,
        "widgetEmptyTextConfiguration": {
            "subSectionKey": "prescriptionTemplateMapping",
            "fieldKey": "pastPrescriptionList",
            "selfFieldKey": "templateIds",
            "isEmptyText": "No past prescription found.",
            "isNotEmptyText": "No past prescription found.",
            "isSelfEmptyText": "No past prescription found."
        },
        "subSectionType": null,
        "dataToBeSaved": true,
        "data": [
            {
                "questionTitle": "",
                "key": "treatmentPlan",
                "answerList": [
                    {
                        "key": "suggestions",
                        "type": "PAST_PRESCRIPTION_LIST",
                        "isSuggestion": true,
                        "recommendationKey": "pastPerscription",
                        "targetFieldParentKey": "templateIds",
                        "parentFieldKey": "disease",
                        "targetField": [
                            {
                                "key": "disease",
                                "primaryTarget": true,
                                "recommendationValue": "diseaseName",
                                "targetValueIsObject": true
                            },
                            {
                                "key": "diagnosticDetails",
                                "primaryTarget": false,
                                "recommendationValue": "diseaseName",
                                "targetValueIsObject": false
                            }
                        ]
                    }
                ]
            }
        ]
    },
    {
        "sectionKey": "Prescription",
        "subSectionKey": "prescriptionDiagnosis",
        "subSectionTitle": "Diagnosis",
        "subSectionDescription": "",
        "showPastHistory": false,
        "showPastHistoryText": null,
        "isDataRequired": false,
        "validationSchema": {
            "$schema": "http://json-schema.org/draft-07/schema#",
            "title": "prescriptionLabTest Schema",
            "type": "object",
            "properties": {}
        },
        "headers": [],
        "colWidth": null,
        "modalConfigurationJson": null,
        "subSectionType": null,
        "dataToBeSaved": true,
        "data": [
            {
                "questionTitle": "",
                "key": "diagnosticsList",
                "type": "fetchData",
                "fetchDataConfiguration": {
                    "method": "getTreatmentPlan",
                    "appendQueryString": true,
                    "params": [
                        "diagnosis",
                    ]
                },
                "trackerKey": "disease.displayValue",
                "uniqueKey": "disease.displayValue",
                "enableSequentialLoading": true,
                "answerList": [
                    {
                        "type": "TEMPLATED_LIST_V2",
                        "openMode": true,
                        "fields": [
                            {
                                "type": "HEADER",
                                "key": "header",
                                "displayAdditionalInfo": false,
                                "metaInfoKey": "",
                                "targetKey": "disease",
                                "inline": false
                            },
                            {
                                "key": "provisional",
                                "type": "TOGGLE_SWITCH",
                                "header": " ",
                                "textSize": "small",
                                "configuration": {
                                    "label": "Provisional?",
                                    "default": false
                                }
                            },
                            {
                                "key": "comment",
                                "type": "TEXT_FIELD",
                                "placeholder": "Comment",
                                "textSize": "medium",
                                "header": " "
                            }
                        ],
                        "defaultItemFields": [
                            {
                                "key": "suggestionsLabel",
                                "type": "LABEL",
                                "labelValue": "Commonly used",
                                "textSize": "xxxsmall"
                            },
                            {
                                "type": "NEWLINE",
                                "key": "emptyRow",
                                "header": "",
                                "inline": false
                            },
                            {
                                "key": "suggestions",
                                "type": "SUGGESTION_LIST",
                                "textSize": "small",
                                "isSuggestion": true,
                                "recommendationKey": "diagnosis",
                                "parentFieldKey": "disease",
                                "targetFields": [
                                    {
                                        "key": "disease",
                                        "primaryTarget": true,
                                        "recommendationValue": "diseaseName",
                                        "targetValueIsObject": true
                                    },
                                    {
                                        "key": "diagnosticDetails",
                                        "primaryTarget": false,
                                        "recommendationValue": "diseaseName",
                                        "targetValueIsObject": false
                                    },
                                    {
                                        "key": "code",
                                        "primaryTarget": false,
                                        "recommendationValue": "id",
                                        "targetValueIsObject": false
                                    }
                                ]
                            },
                            {
                                "type": "NEWLINE",
                                "key": "emptyRow",
                                "header": "",
                                "inline": false
                            },
                            {
                                "key": "disease",
                                "type": "TYPEAHEAD",
                                "linkedFieldKeys": [
                                    {
                                        "key": "diagnosticDetails",
                                        "valueKey": "name"
                                    }
                                ],
                                "inline": false,
                                "typeAheadConfiguration": {
                                    "source": "MAVERICK",
                                    "url": "/maverick/care/disease_catalogue",
                                    "appendQueryString": true,
                                    "otherKeysMapping": {},
                                    "additionalInfoLabelKeys": [
                                        "id",
                                        "name"
                                    ],
                                    "valueKey": "name",
                                    "labelKey": "name",
                                    "internalKey": "alternateNames",
                                    "inputParam": "q",
                                    "resultMapper": [
                                        {
                                            "internalValue": "internalValue",
                                            "displayValue": "displayValue"
                                        }
                                    ],
                                    "code": "id",
                                    "allowAddItem": true,
                                    "addItemToTypeahead": {
                                        "displayText": "Click to add new"
                                    }
                                },
                                "placeholder": "Search",
                                "header": "DIAGNOSIS",
                                "enableSearchIcon": true
                            }
                        ]
                    }
                ]
            }
        ]
    },
    {
        "sectionKey": "Prescription",
        "subSectionKey": "prescriptionTemplateMapping",
        "subSectionTitle": "Treatment Plans",
        "subSectionDescription": "Select a Treatment Plan to add the details (Medicines, Lab Tests, Advice, Referral and Follow Up) from it.",
        "showPastHistory": false,
        "showPastHistoryText": null,
        "isDataRequired": false,
        "FeatureConfigHide": {
            "key": "OrionEnablePrescriptionTemplate",
            "allowedOnMobile": true
        },
        "colWidth": null,
        "ViewModalConfiguration": {
            "title": "SEE TREATMENT PLANS",
            "modalComponent": "TreatmentPlansModal"
        },
        "widgetEmptyTextConfiguration": {
            "subSectionKey": "prescriptionDiagnosis",
            "fieldKey": "diagnosticsList",
            "selfFieldKey": "templateIds",
            "isEmptyText": "Please select a Diagnosis to view Treatment Plans",
            "isNotEmptyText": "No Treatment Plans have been created for these Diagnoses",
            "isSelfEmptyText": "All Treatment Plans for these Diagnoses have been applied"
        },
        "subSectionType": null,
        "dataToBeSaved": true,
        "data": [
            {
                "questionTitle": "",
                "key": "treatmentPlan",
                "answerList": [
                    {
                        "key": "suggestions",
                        "type": "TREATMENT_PLAN_LIST",
                        "isSuggestion": true,
                        "recommendationKey": "treatmentPlan",
                        "targetFieldParentKey": "templateIds",
                        "parentFieldKey": "disease",
                        "targetField": [
                        ]
                    }
                ]
            }
        ]
    },
    {
        "sectionKey": "Prescription",
        "subSectionKey": "prescriptionLabTest",
        "subSectionTitle": "Lab test",
        "subSectionDescription": "",
        "showPastHistory": false,
        "showPastHistoryText": null,
        "isDataRequired": false,
        "validationSchema": {
            "$schema": "http://json-schema.org/draft-07/schema#",
            "title": "prescriptionLabTest Schema",
            "type": "object",
            "properties": {
                "labTestList": {
                    "type": "array",
                    "items": {
                        "type": "object",
                        "properties": {
                            "diagnosis": {
                                "type": "object",
                                "properties": {
                                    "displayValue": {
                                        "type": "string",
                                        "minLength": 1
                                    }
                                }
                            },
                            "labTestFrequency": {
                                "type": "object",
                                "properties": {
                                    "unit": {
                                        "type": "string"
                                    },
                                    "count": {
                                        "type": "string"
                                    }
                                }
                            },
                            "instructions": {
                                "type": "string"
                            }
                        },
                        "required": [
                            "diagnosis",
                            "labTestFrequency"
                        ]
                    }
                }
            }
        },
        "colWidth": null,
        "modalConfigurationJson": null,
        "subSectionType": null,
        "dataToBeSaved": true,
        "data": [
            {
                "questionTitle": "",
                "key": "labTestList",
                "type": "preFill",
                "preFillConfiguration": {
                    "source": "CARE_RECOMMENDATION_SERVICE",
                    "url": "/care-recommendation-service/lab-test-meta/search",
                    "appendQueryString": true,
                    "hyphenated": true,
                    "params": [
                        "doctorId",
                        "code",
                        "subServiceType"
                    ],
                    "parseResult": {
                        "key": "recommendation"
                    }
                },
                "trackerKey": "diagnosis.displayValue",
                "uniqueKey": "code",
                "enableSequentialLoading": true,
                "answerList": [
                    {
                        "type": "TEMPLATED_LIST_V2",
                        "fields": [
                            {
                                "type": "HEADER",
                                "key": "header",
                                "displayAdditionalInfo": false,
                                "metaInfoKey": "",
                                "targetKey": "diagnosis",
                                "inline": false
                            },
                            {
                                "type": "NEWLINE",
                                "key": "emptyRow",
                                "header": "",
                                "inline": false
                            },
                            {
                                "type": "ADVANCED_DURATION",
                                "key": "labTestFrequency",
                                "header": "After",
                                "placeholder": "Custom",
                                "mandatory": true,
                                "toggleValues": [
                                    {
                                        "internalValue": "STAT",
                                        "displayValue": "Stat",
                                        "suggestByDefault": false
                                    },
                                    {
                                        "internalValue": "DAY",
                                        "displayValue": "Day",
                                        "suggestByDefault": true
                                    },
                                    {
                                        "internalValue": "WEEK",
                                        "displayValue": "Week",
                                        "suggestByDefault": true
                                    },
                                    {
                                        "internalValue": "MONTH",
                                        "displayValue": "Month",
                                        "suggestByDefault": true
                                    }
                                ],
                                "unitList": [
                                    {
                                        "displayValue": "Day",
                                        "internalValue": "DAY"
                                    },
                                    {
                                        "displayValue": "Month",
                                        "internalValue": "MONTH"
                                    },
                                    {
                                        "displayValue": "Year",
                                        "internalValue": "YEAR"
                                    },
                                    {
                                        "displayValue": "Week",
                                        "internalValue": "WEEK"
                                    }
                                ]
                            },
                            {
                                "key": "instructions",
                                "type": "BOOLEAN_TOGGLE",
                                "values": [
                                    {
                                        "displayValue": "Random",
                                        "internalValue": "Random"
                                    },
                                    {
                                        "displayValue": "Fasting",
                                        "internalValue": "Fasting"
                                    }
                                ],
                                "header": "Instructions",
                                "defaultValue": "Random",
                                "textSize": "xsmall"
                            },
                            {
                                "key": "comments",
                                "type": "TEXT_FIELD",
                                "placeholder": "Comment",
                                "textSize": "medium",
                                "header": "Other Instructions",
                                "suggestions": [
                                    "If symptoms persist"
                                ]
                            }
                        ],
                        "defaultItemFields": [
                            {
                                "key": "suggestionsLabel",
                                "type": "LABEL",
                                "labelValue": "Commonly used",
                                "textSize": "xxxsmall"
                            },
                            {
                                "type": "NEWLINE",
                                "key": "emptyRow",
                                "header": "",
                                "inline": false
                            },
                            {
                                "key": "suggestions",
                                "type": "SUGGESTION_LIST",
                                "textSize": "small",
                                "isSuggestion": true,
                                "recommendationKey": "labTests",
                                "parentFieldKey": "diagnosis",
                                "targetFields": [
                                    {
                                        "key": "diagnosis",
                                        "primaryTarget": true,
                                        "recommendationValue": "name",
                                        "targetValueIsObject": true
                                    },
                                    {
                                        "key": "code",
                                        "primaryTarget": false,
                                        "recommendationValue": "code",
                                        "targetValueIsObject": false
                                    },
                                    {
                                        "key": "code",
                                        "primaryTarget": false,
                                        "recommendationValue": "code",
                                        "targetValueIsObject": false
                                    }
                                ]
                            },
                            {
                                "type": "NEWLINE",
                                "key": "emptyRow",
                                "header": "",
                                "inline": false
                            },
                            {
                                "key": "diagnosis",
                                "type": "TYPEAHEAD",
                                "targetFieldKey": "diagnosis",
                                "inline": false,
                                "typeAheadConfiguration": {
                                    "source": "MAVERICK",
                                    "url": "/maverick/care/diagnostic_catalogue",
                                    "appendQueryString": true,
                                    "inputParam": "q",
                                    "valueKey": "name",
                                    "labelKey": "name",
                                    "internalKey": "alternateNames",
                                    "additionalInfoLabelKeys": [
                                        "alternateNames"
                                    ],
                                    "additionalParams": {
                                        "result-size": 20,
                                        "productStatus": "LIVE",
                                        "isSellable": true
                                    },
                                    "code": "code",
                                    "allowAddItem": true,
                                    "addItemToTypeahead": {
                                        "displayText": "Click to add new"
                                    },
                                    "otherKeysMapping": {
                                        "prescribableProducts": "prescribableProducts"
                                    }
                                },
                                "placeholder": "Search",
                                "header": "",
                                "enableSearchIcon": true
                            }
                        ]
                    }
                ]
            }
        ]
    },
    {
        "sectionKey": "Prescription",
        "subSectionKey": "prescriptionMedicines",
        "subSectionTitle": "Medicines",
        "subSectionDescription": "",
        "showPastHistory": false,
        "showPastHistoryText": null,
        "isDataRequired": false,
        "validationSchema": {
            "type": "object",
            "$schema": "http://json-schema.org/draft-07/schema#",
            "title": "prescriptionMedication SchemaValidation ",
            "properties": {
                "prescriptionMedicationList": {
                    "type": "array",
                    "items": {
                        "type": "object",
                        "properties": {
                            "brand": {
                                "type": "object",
                                "properties": {
                                    "displayValue": {
                                        "type": "string",
                                        "minLength": 1
                                    },
                                    "internalValue": {
                                        "type": "string",
                                        "minLength": 1
                                    }
                                },
                                "required": [
                                    "internalValue"
                                ]
                            },
                            "genericName": {
                                "type": "object",
                                "properties": {
                                    "displayValue": {
                                        "type": "string",
                                        "minLength": 1
                                    },
                                    "internalValue": {
                                        "type": "string",
                                        "minLength": 1
                                    }
                                }
                            },
                            "medicineType": {
                                "type": "string"
                            },
                            "dosage": {
                                "type": "string"
                            }
                        },
                        "required": [
                            "brand",
                            // "genericName"
                        ]
                    }
                }
            }
        },
        "colWidth": null,
        "modalConfigurationJson": null,
        "subSectionType": null,
        "dataToBeSaved": true,
        "data": [
            {
                "questionTitle": "",
                "key": "prescriptionMedicationList",
                "type": "preFill",
                "preFillConfiguration": {
                    "source": "ALBUS",
                    "url": "/albus/recommendation/product",
                    "appendQueryString": true,
                    "params": [
                        "doctorId",
                        "uCode",
                        "subServiceType"
                    ]
                },
                "trackerKey": "brand.displayValue",
                "uniqueKey": "code",
                "enableSequentialLoading": true,
                "answerList": [
                    {
                        "type": "TEMPLATED_LIST_V2",
                        "fields": [
                            {
                                "type": "HEADER",
                                "key": "header",
                                "displayAdditionalInfo": true,
                                "additionalInfoSet": [
                                    {
                                        "type": "header",
                                        "value": "Additional Info:"
                                    },
                                    {
                                        "type": "item",
                                        "key": "genericName",
                                        "itemType": "staticDataValue"
                                    },
                                    {
                                        "type": "item",
                                        "key": "dosage"
                                    },
                                    {
                                        "type": "item",
                                        "key": "medicineType"
                                    }
                                ],
                                "targetKey": "brand",
                                "mandateAdditionalInfo": ["genericName", "dosage", "medicineType"],
                                "inline": false
                            },
                            {
                                "type": "NEWLINE",
                                "key": "emptyRow",
                                "header": "",
                                "inline": false
                            },
                            {
                                "key": "medicineQuantity",
                                "type": "INPUT_WITH_DROPDOWN",
                                "inputOptions": ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"],
                                "dropdownConfiguration": {
                                    "source": "STATIC",
                                    "options": [
                                        {
                                            "internalValue": "TAB",
                                            "displayValue": "TAB"
                                        },
                                        {
                                            "internalValue": "CAP",
                                            "displayValue": "CAP"
                                        },
                                        {
                                            "internalValue": "ML",
                                            "displayValue": "ML"
                                        },
                                        {
                                            "internalValue": "GM",
                                            "displayValue": "GM"
                                        },
                                        {
                                            "internalValue": "TBSP",
                                            "displayValue": "TBSP"
                                        },
                                        {
                                            "internalValue": "TSP",
                                            "displayValue": "TSP"
                                        },
                                        {
                                            "internalValue": "MG",
                                            "displayValue": "MG"
                                        },
                                        {
                                            "internalValue": "UNITS",
                                            "displayValue": "UNITS"
                                        },
                                        {
                                            "internalValue": "SPRAY",
                                            "displayValue": "SPRAY"
                                        },
                                        {
                                            "internalValue": "PUFFS",
                                            "displayValue": "PUFFS"
                                        },
                                        {
                                            "internalValue": "DROPS",
                                            "displayValue": "DROPS"
                                        },
                                        {
                                            "internalValue": "MCG",
                                            "displayValue": "MCG"
                                        },
                                        {
                                            "internalValue": "PG",
                                            "displayValue": "PG"
                                        },
                                        {
                                            "internalValue": "IU",
                                            "displayValue": "IU"
                                        }
                                    ]
                                },
                                "header": "Quantity",
                                "textSize": "xsmall",
                                "inputPlaceholder": "value",
                                "dropdownPlaceholder": "unit"
                            },
                            {
                                "key": "medicineDosage",
                                "type": "DROPDOWN",
                                "dropdownConfiguration": {
                                    "source": "STATIC",
                                    "options": [
                                        {
                                            "displayValue": "1-1-0",
                                            "internalValue": "1-1-0"
                                        },
                                        {
                                            "displayValue": "0-0-1",
                                            "internalValue": "0-0-1"
                                        },
                                        {
                                            "displayValue": "1-0-0",
                                            "internalValue": "1-0-0"
                                        },
                                        {
                                            "displayValue": "0-1-1",
                                            "internalValue": "0-1-1"
                                        },
                                        {
                                            "displayValue": "Every 6 hours",
                                            "internalValue": "Every 6 hours"
                                        },
                                        {
                                            "displayValue": "Every 4 hours",
                                            "internalValue": "Every 4 hours"
                                        },
                                        {
                                            "displayValue": "Hourly",
                                            "internalValue": "Hourly"
                                        }
                                    ]
                                },
                                "defaultValue": "Other",
                                "header": "Timing",
                                "textSize": "xsmall",
                                "suggestions": [
                                    {
                                        "displayValue": "1-0-1",
                                        "internalValue": "1-0-1"
                                    },
                                    {
                                        "displayValue": "1-1-1",
                                        "internalValue": "1-1-1"
                                    },
                                    {
                                        "displayValue": "0-1-0",
                                        "internalValue": "0-1-0"
                                    },
                                ]
                            },
                            {
                                "key": "medicineInstructions",
                                "type": "DROPDOWN",
                                "dropdownConfiguration": {
                                    "source": "STATIC",
                                    "options": [
                                        {
                                            "internalValue": "BEFORE_SLEEP",
                                            "displayValue": "Before sleep"
                                        },
                                        // {
                                        //     "internalValue": "BELOW_THE_TONGUE",
                                        //     "displayValue": "Below the tongue"
                                        // },
                                        {
                                            "internalValue": "EMPTY_STOMACH",
                                            "displayValue": "Empty stomach"
                                        },
                                        // {
                                        //     "internalValue": "CHEWABLE",
                                        //     "displayValue": "Chewable"
                                        // },
                                        // {
                                        //     "internalValue": "PER_ORAL",
                                        //     "displayValue": "Per Oral"
                                        // },
                                        // {
                                        //     "internalValue": "INTRADERMAL_INJECTION",
                                        //     "displayValue": "Intradermal Injection"
                                        // },
                                        // {
                                        //     "internalValue": "INTRAMUSCULAR",
                                        //     "displayValue": "Intramuscular"
                                        // },
                                        // {
                                        //     "internalValue": "INTRAVENOUS",
                                        //     "displayValue": "Intravenous"
                                        // },
                                        // {
                                        //     "internalValue": "SUBCUTANEOUS",
                                        //     "displayValue": "Subcutaneous"
                                        // },
                                        // {
                                        //     "internalValue": "NASAL_SPRAY",
                                        //     "displayValue": "Nasal Spray"
                                        // },
                                        // {
                                        //     "internalValue": "PER_VAGINAL",
                                        //     "displayValue": "Per Vaginal"
                                        // },
                                        // {
                                        //     "internalValue": "PER_RECTUM",
                                        //     "displayValue": "Per Rectum"
                                        // },
                                        // {
                                        //     "internalValue": "LOCAL_APPLICATION",
                                        //     "displayValue": "Local Application"
                                        // },
                                        // {
                                        //     "internalValue": "SUBLINGUAL",
                                        //     "displayValue": "Sublingual"
                                        // },
                                        // {
                                        //     "internalValue": "TRANSDERMAL",
                                        //     "displayValue": "Transdermal"
                                        // }
                                    ]
                                },
                                "defaultValue": "Others",
                                "header": "Intake",
                                "textSize": "small",
                                "suggestions": [
                                    {
                                        "internalValue": "BEFORE_FOOD",
                                        "displayValue": "Before food"
                                    },
                                    {
                                        "internalValue": "AFTER_FOOD",
                                        "displayValue": "After food"
                                    }
                                ]
                            },
                            {
                                "key": "frequency",
                                "type": "DROPDOWN",
                                "dropdownConfiguration": {
                                    "source": "STATIC",
                                    "options": [
                                        {
                                            "displayValue": "Daily",
                                            "internalValue": "Daily"
                                        },
                                        {
                                            "displayValue": "Alternate Day",
                                            "internalValue": "Alternate Day"
                                        },
                                        {
                                            "displayValue": "Every week",
                                            "internalValue": "Every week"
                                        },
                                        {
                                            "displayValue": "Every 15 days",
                                            "internalValue": "Every 15 days"
                                        },
                                        {
                                            "displayValue": "Every 30 days",
                                            "internalValue": "Every 30 days"
                                        },
                                        {
                                            "displayValue": "Every 3 months",
                                            "internalValue": "Every 3 months"
                                        }
                                    ]
                                },
                                "placeholder": "",
                                "defaultValue": "Daily",
                                "header": "Frequency",
                                "textSize": "xsmall"
                            },
                            {
                                "type": "ADVANCED_DURATION",
                                "key": "medicineDuration",
                                "header": "Duration",
                                "placeholder": "Custom",
                                "toggleValues": [
                                    {
                                        "internalValue": "SOS",
                                        "displayValue": "SOS",
                                        "suggestByDefault": false
                                    },
                                    {
                                        "internalValue": "DAY",
                                        "displayValue": "Day",
                                        "suggestByDefault": true
                                    },
                                    {
                                        "internalValue": "WEEK",
                                        "displayValue": "Week",
                                        "suggestByDefault": true
                                    },
                                    {
                                        "internalValue": "MONTH",
                                        "displayValue": "Month",
                                        "suggestByDefault": true
                                    }
                                ]
                            },
                            {
                                "key": "comments",
                                "type": "TEXT_FIELD",
                                "placeholder": "Comment",
                                "textSize": "medium",
                                "header": " "
                            }
                        ],
                        "defaultItemFields": [
                            {
                                "key": "suggestionsLabel",
                                "type": "LABEL",
                                "labelValue": "Commonly used",
                                "textSize": "xxxsmall"
                            },
                            {
                                "type": "NEWLINE",
                                "key": "emptyRow",
                                "header": "",
                                "inline": false
                            },
                            {
                                "key": "suggestions",
                                "type": "SUGGESTION_LIST",
                                "textSize": "small",
                                "isSuggestion": true,
                                "recommendationKey": "medicines",
                                "parentFieldKey": "brand",
                                "targetFields": [
                                    {
                                        "key": "brand",
                                        "primaryTarget": true,
                                        "recommendationValue": "productName",
                                        "targetValueIsObject": true
                                    },
                                    {
                                        "key": "genericName",
                                        "primaryTarget": false,
                                        "recommendationValue": "molecule",
                                        "targetValueIsObject": true
                                    },
                                    {
                                        "key": "medicineType",
                                        "primaryTarget": false,
                                        "recommendationValue": "dosageForm",
                                        "targetValueIsObject": false
                                    },
                                    {
                                        "key": "dosage",
                                        "primaryTarget": false,
                                        "recommendationValue": "strength",
                                        "targetValueIsObject": false
                                    },
                                    {
                                        "key": "frequency",
                                        "defaultValue": "Daily",
                                        "targetValueIsObject": false
                                    },
                                    {
                                        "key": "code",
                                        "recommendationValue": "code",
                                        "targetValueIsObject": false
                                    },
                                    {
                                        "key": "templateId",
                                        "primaryTarget": false,
                                        "recommendationValue": "code",
                                        "targetValueIsObject": false
                                    }
                                ]
                            },
                            {
                                "type": "NEWLINE",
                                "key": "emptyRow",
                                "header": "",
                                "inline": false
                            },
                            {
                                "key": "brand",
                                "type": "TYPEAHEAD",
                                "inline": false,
                                "linkedFieldKeys": [
                                    {
                                        "key": "genericName",
                                        "valueKey": "molecule",
                                        "primaryType": "object"
                                    },
                                    {
                                        "key": "medicineType",
                                        "valueKey": "dosageForm"
                                    },
                                    {
                                        "key": "dosage",
                                        "valueKey": "strength"
                                    },
                                    {
                                        "key": "medicineQuantity",
                                        "valueKey": "dosageForm",
                                        "primaryType": "frequency",
                                        "valueToUnitMap": {
                                            "TABLET": "TAB",
                                            "CAPSULES": "CAP",
                                            "SYRUP": "ML",
                                            "INJECTION": "ML",
                                            "POWDER": "GM",
                                            "EYE DROPS": "DROPS",
                                            "SOLUTION": "ML",
                                            "INHALER": "PUFFS",
                                            "DROPS": "DROPS",
                                            "OIL": "ML",
                                            "STRIPS": "UNITS",
                                            "GRANULES": "UNITS",
                                            "NASAL SPRAY": "SPRAY",
                                            "ROTACAPS": "PUFFS",
                                            "EAR EYE DROPS": "DROPS",
                                            "EAR DROPS": "DROPS",
                                            "VACCINE": "ML",
                                            "PAIN SPRAY": "SPRAY",
                                            "SYRINGES": "ML",
                                            "HAIR OIL": "ML",
                                            "CONDITIONER": "ML",
                                            "MICROGRAM": "MG",
                                            "PICOGRAM": "PG",
                                            "INTERNATIONAL UNITS": "IU"
                                        },
                                        "countToUnitMap": {
                                            "TABLET": "1",
                                            "CAPSULES": "1"
                                        }
                                    },
                                    {
                                        "key": "frequency",
                                        "defaultValue": "Daily"
                                    }
                                ],
                                "typeAheadConfiguration": {
                                    "source": "MAVERICK",
                                    "url": "/maverick/care/medicine_catalogue",
                                    "appendQueryString": true,
                                    "additionalParams": {
                                        "result-size": 20
                                    },
                                    "otherKeysMapping": {
                                        "available": "expectedInCareFitPharmacy"
                                    },
                                    "highlightOn": "available",
                                    "valueKey": "productName",
                                    "labelKey": "productName",
                                    "additionalInfoLabelKeys": [
                                        "molecule",
                                        "dosageForm",
                                        "strength"
                                    ],
                                    "inputParam": "q",
                                    "resultMapper": [
                                        {
                                            "internalValue": "internalValue",
                                            "displayValue": "displayValue"
                                        }
                                    ],
                                    "internalKey": "productName",
                                    "code": "uCode",
                                    "allowAddItem": true,
                                    "addItemToTypeahead": {
                                        "displayText": "Click to add new"
                                    }
                                },
                                "placeholder": "Search",
                                "header": "",
                                "enableSearchIcon": true
                            }
                        ]
                    }
                ]
            }
        ]
    },
    {
        "sectionKey": "Prescription",
        "subSectionKey": "prescriptionAdvice",
        "subSectionTitle": "Advice",
        "subSectionDescription": "",
        "showPastHistory": false,
        "showPastHistoryText": null,
        "isDataRequired": false,
        "validationSchema": {
            "$schema": "http://json-schema.org/draft-07/schema#",
            "title": "prescriptionLabTest Schema",
            "type": "object",
            "properties": {}
        },
        "colWidth": null,
        "modalConfigurationJson": null,
        "subSectionType": null,
        "dataToBeSaved": true,
        "data": [
            {
                "questionTitle": "",
                "key": "advice",
                "trackerKey": "doctorAdvice.displayValue",
                "uniqueKey": "doctorAdvice.displayValue",
                "answerList": [
                    {
                        "type": "TEMPLATED_LIST_V2",
                        "openMode": true,
                        "fields": [
                            {
                                "key": "doctorAdvice",
                                "type": "TEXT_FIELD",
                                "placeholder": "",
                                "header": " ",
                                "inline": false,
                                "isObject": true
                            }
                        ],
                        "defaultItemFields": [
                            {
                                "key": "suggestionsLabel",
                                "type": "LABEL",
                                "labelValue": "Commonly used",
                                "textSize": "xxxsmall"
                            },
                            {
                                "type": "NEWLINE",
                                "key": "emptyRow",
                                "header": "",
                                "inline": false
                            },
                            {
                                "key": "suggestions",
                                "type": "SUGGESTION_LIST",
                                "textSize": "small",
                                "isSuggestion": true,
                                "recommendationKey": "lifeStyleAdvices",
                                "parentFieldKey": "doctorAdvice",
                                "targetFields": [
                                    {
                                        "key": "doctorAdvice",
                                        "primaryTarget": true,
                                        "recommendationValue": "name",
                                        "targetValueIsObject": true
                                    },
                                    {
                                        "key": "templateId",
                                        "primaryTarget": false,
                                        "recommendationValue": "id",
                                        "targetValueIsObject": false
                                    }
                                ]
                            },
                            {
                                "type": "NEWLINE",
                                "key": "emptyRow",
                                "header": "",
                                "inline": false
                            },
                            {
                                "key": "doctorAdvice",
                                "type": "TYPEAHEAD",
                                "inline": false,
                                "typeAheadConfiguration": {
                                    "source": "MAVERICK",
                                    "url": "/maverick/care/lifestyle_advice_catalogue",
                                    "appendQueryString": true,
                                    "inputParam": "q",
                                    "valueKey": "name",
                                    "labelKey": "name",
                                    "internalKey": "displayName",
                                    "additionalInfoLabelKeys": [
                                        "alternateNames"
                                    ],
                                    "additionalParams": {
                                        "result-size": 20
                                    },
                                    "allowAddItem": true,
                                    "addItemToTypeahead": {
                                        "displayText": "Click to add new"
                                    }
                                },
                                "placeholder": "Search",
                                "header": "",
                                "enableSearchIcon": true
                            }
                        ]
                    }
                ]
            }
        ]
    },
    {
        "sectionKey": "Prescription",
        "subSectionKey": "prescriptionFollowUp",
        "subSectionTitle": "Follow up",
        "subSectionDescription": "",
        "showPastHistory": false,
        "showPastHistoryText": null,
        "isDataRequired": false,
        "colWidth": null,
        "modalConfigurationJson": null,
        "subSectionType": null,
        "headers": [
            {
                "label": "refer after",
                "display": "REFER AFTER",
                "mandatory": false,
                "colSize": "large"
            },
            {
                "label": "safety netting",
                "display": "SAFETY NETTING",
                "mandatory": false,
                "colSize": "large"
            }
        ],
        "dataToBeSaved": true,
        "data": [
            {
                "questionTitle": "",
                "key": "followup",
                "answerList": [
                    {
                        "type": "ADVANCED_DURATION",
                        "key": "followUp",
                        "header": "Duration",
                        "textSize": "large",
                        "toggleValues": [
                            {
                                "internalValue": "DAY",
                                "displayValue": "Day",
                                "suggestByDefault": true
                            },
                            {
                                "internalValue": "WEEK",
                                "displayValue": "Week",
                                "suggestByDefault": true
                            },
                            {
                                "internalValue": "MONTH",
                                "displayValue": "Month",
                                "suggestByDefault": true
                            }
                        ]
                    },
                    {
                        "key": "comment",
                        "type": "INPUT",
                        "placeholder": "Comment",
                        "textSize": "large",
                        "header": " "
                    }
                ]
            }
        ]
    },
    {
        "sectionKey": "Prescription",
        "subSectionKey": "prescriptionReferral",
        "subSectionTitle": "Referral",
        "subSectionDescription": "",
        "showPastHistory": false,
        "showPastHistoryText": null,
        "isDataRequired": false,
        "colWidth": null,
        "modalConfigurationJson": null,
        "subSectionType": null,
        "dataToBeSaved": true,
        "data": [
            {
                "questionTitle": "",
                "key": "referral",
                "trackerKey": "doctorSpecialityCode.displayValue",
                "uniqueKey": "doctorSpecialityCode.displayValue",
                "answerList": [
                    {
                        "type": "TEMPLATED_LIST_V2",
                        "openMode": true,
                        "fields": [
                            {
                                "type": "HEADER",
                                "key": "header",
                                "displayAdditionalInfo": false,
                                "targetKey": "doctorSpecialityCode",
                                "textSize": "small",
                                "header": " ",
                            },
                            {
                                "key": "comments",
                                "type": "TEXT_FIELD",
                                "placeholder": "Comment",
                                "textSize": "large",
                                "header": "Other Advice",
                            }
                        ],
                        "defaultItemFields": [
                            {
                                "key": "doctorSpecialityCode",
                                "type": "TYPEAHEAD",
                                "typeAheadConfiguration": {
                                    "source": "ORION",
                                    "url": "/orion/static/fetch/referral",
                                    "valueKey": "displayValue",
                                    "labelKey": "displayValue",
                                    "internalKey": "internalValue",
                                    "appendQueryString": true,
                                    "inputParam": "q",
                                    "allowAddItem": false,
                                },
                                "placeholder": "Others",
                                "header": "Intake",
                                "inline": false,
                                "enableSearchIcon": true
                            },
                        ]
                    }
                ]
            }
        ]
    }
]

export const RECOMMENDATIONS_SECTIONS: { [key: string]: string[] } = {
    GP: [SUB_SECTIONS_CONTEXT_KEY.CHIEF_COMPLAINTS, SUB_SECTIONS_CONTEXT_KEY.LIFESTYLE_ADVICE],
    AYURVEDA: [SUB_SECTIONS_CONTEXT_KEY.CHIEF_COMPLAINTS, SUB_SECTIONS_CONTEXT_KEY.LIFESTYLE_ADVICE],
    GP_STANDARD: [SUB_SECTIONS_CONTEXT_KEY.CHIEF_COMPLAINTS, SUB_SECTIONS_CONTEXT_KEY.LIFESTYLE_ADVICE],
    INTERNAL_MEDICINE: [SUB_SECTIONS_CONTEXT_KEY.CHIEF_COMPLAINTS, SUB_SECTIONS_CONTEXT_KEY.LIFESTYLE_ADVICE],
    CARDIOLOGIST: [SUB_SECTIONS_CONTEXT_KEY.CHIEF_COMPLAINTS, SUB_SECTIONS_CONTEXT_KEY.LIFESTYLE_ADVICE],
    DERMATOLOGIST: [SUB_SECTIONS_CONTEXT_KEY.CHIEF_COMPLAINTS, SUB_SECTIONS_CONTEXT_KEY.LIFESTYLE_ADVICE],
    ENT: [SUB_SECTIONS_CONTEXT_KEY.CHIEF_COMPLAINTS, SUB_SECTIONS_CONTEXT_KEY.LIFESTYLE_ADVICE],
    GYNAECOLOGIST: [SUB_SECTIONS_CONTEXT_KEY.CHIEF_COMPLAINTS, SUB_SECTIONS_CONTEXT_KEY.LIFESTYLE_ADVICE],
    HCU_GP_IM: [SUB_SECTIONS_CONTEXT_KEY.CHIEF_COMPLAINTS, SUB_SECTIONS_CONTEXT_KEY.LIFESTYLE_ADVICE],
    MIND_THERAPIST: [SUB_SECTIONS_CONTEXT_KEY.CHIEF_COMPLAINTS, SUB_SECTIONS_CONTEXT_KEY.LIFESTYLE_ADVICE],
    ANXIETY_THERAPIST: [SUB_SECTIONS_CONTEXT_KEY.CHIEF_COMPLAINTS, SUB_SECTIONS_CONTEXT_KEY.LIFESTYLE_ADVICE],
    ORTHOPEDIC: [SUB_SECTIONS_CONTEXT_KEY.CHIEF_COMPLAINTS, SUB_SECTIONS_CONTEXT_KEY.LIFESTYLE_ADVICE],
    PEDIATRICIAN: [SUB_SECTIONS_CONTEXT_KEY.CHIEF_COMPLAINTS, SUB_SECTIONS_CONTEXT_KEY.LIFESTYLE_ADVICE],
    PSYCHIATRIST: [SUB_SECTIONS_CONTEXT_KEY.CHIEF_COMPLAINTS, SUB_SECTIONS_CONTEXT_KEY.LIFESTYLE_ADVICE],
    PHYSIOTHERAPIST: [SUB_SECTIONS_CONTEXT_KEY.CHIEF_COMPLAINTS, SUB_SECTIONS_CONTEXT_KEY.LIFESTYLE_ADVICE],
    LC: Object.keys(MealSlots).map(i => `meals-${i.toLowerCase()}`)
};

export const SEQUENTIAL_RECOMMENDATIONS_SECTIONS = {
    [SUB_SECTIONS_CONTEXT_KEY.DIAGNOSIS]: {
        url: "diagnosis-recommendation",
        labelKey: "diseaseName",
        isSequentialNeeded: true
    },
    [SUB_SECTIONS_CONTEXT_KEY.LAB_TESTS]: {
        url: "lab-test-recommendation",
        labelKey: "name",
        isSequentialNeeded: true
    },
    [SUB_SECTIONS_CONTEXT_KEY.MEDICINES]: {
        url: "medicine-recommendation",
        labelKey: "productName",
        isSequentialNeeded: true
    },
    [SUB_SECTIONS_CONTEXT_KEY.BODY_PARTS]: {
        url: "body-part-recommendation",
        labelKey: "displayName",
        isSequentialNeeded: false
    }
}

export const ALLOWED_ACTIONS = {
    START_CONSULTATION: "START_CONSULTATION",
    EDIT_CONSULTATION: "EDIT_CONSULTATION"
}

export const AdditionalSectionsToSaveInDraft: string[] = [TabNames.EXAMINATIONS]

export const RANGOON_PARAMS = {
    url: "/iris/campaigns/send",
    Authorization: "Authorization: bearer GPK4Y8nsCJJErza3v1FWSUvQYJPzS9iaJgV85RwucHLn",
    campaignId: "CARE_ORION_CALLING",
    creativeIds: ["CLICK_TO_CALL_ORION_CALLING"]
}

export enum SHIFT_STATUS {
    ACTIVE = "ACTIVE",
    TEMP_INACTIVE = "TEMP_INACTIVE",
    INACTIVE = "INACTIVE"
}

export enum ConsultationPageTypes {
    ONGOING = "ONGOING",
    EDIT = "EDIT"
}

export enum CheckinType {
    REFERRAL = "REFERRAL",
    FOLLOWUP = "FOLLOWUP"
}

export enum toastNotificationAppearance {
    success = "success",
    info = "info",
    error = "error"
}

export const CONSULTATION_CHANNELS = {
    AUDIO: "AUDIO",
    VIDEO: "VIDEO"
}

export enum ReferenceEntities {
    DOCTOR = "DOCTOR"
}

export enum FeatureConfigValues {
    TRUE = "TRUE",
    FALSE = "FALSE"
}

export const FeatureConfigInfoKeySearchMap: Array<IFeatureConfigKeySearchMap> = [
    { key: "OrionEnablePrescriptionTemplate", params: [ReferenceEntities.DOCTOR], subSectionPresent: { sectionKey: "Prescription", subSectionKey: "prescriptionTemplateMapping" } }
]

export const BROWSER_NAME = {
    firefox: "Firefox",
    chrome: "Chrome",
    safari: "Safari",
    opera: "Opera",
    internetExplorer: "InternetExplorer",
    edge: "Edge",
    edgeChromium: "EdgeChromium",
    blink: "Blink",
    other: "Other"
};

export enum PushNotificationPlatform {
    DOCS_APP = "DOCS_APP",
    DOCS_APP_MWEB = "DOCS_APP_MWEB"
}

export enum PrescriptionTabModes {
    ONGOING = "ONGOING",
    EDIT = "EDIT",
    TREATMENT_PLAN = "TREATMENT_PLAN"
}

export enum NotificationPermissionStatus {
    granted = "granted",
    default = "default",
    denied = "denied"
}

export const GCMBannerConfig: { [key: string]: any } = {
    numOfDays: 1,
    maxNumOfTimesBannerShown: 3
}

export const CALENDAR_DAYS: { [key: string]: any } = {
    Monday: { label: "Monday", value: "MONDAY", momentDayValue: "Monday", shortForm: "Mon" },
    Tuesday: { label: "Tuesday", value: "TUESDAY", momentDayValue: "Tuesday", shortForm: "Tue" },
    Wednesday: { label: "Wednesday", value: "WEDNESDAY", momentDayValue: "Wednesday", shortForm: "Wed" },
    Thursday: { label: "Thursday", value: "THURSDAY", momentDayValue: "Thursday", shortForm: "Thu" },
    Friday: { label: "Friday", value: "FRIDAY", momentDayValue: "Friday", shortForm: "Fri" },
    Saturday: { label: "Saturday", value: "SATURDAY", momentDayValue: "Saturday", shortForm: "Sat" },
    Sunday: { label: "Sunday", value: "SUNDAY", momentDayValue: "Sunday", shortForm: "Sun" }
}

export enum DEFAULT_WORKING_HOURS_ACTIONS {
    ADD,
    EDIT,
    COPY
}

export enum SUPPORTED_DOCTOR_TYPES_FOR_PACK_DETAILS {
    LIFESTYLE_COACH = "LIFESTYLE_COACH"
}

export const BODY_PARTS_WIDGET: any = {
    "sectionKey": "Prescription",
    "subSectionKey": "prescriptionBodyPartsCateredTo",
    "subSectionTitle": "Body Parts Catered To",
    "subSectionDescription": "",
    "showPastHistory": false,
    "showPastHistoryText": null,
    "isDataRequired": false,
    "colWidth": null,
    "modalConfigurationJson": null,
    "subSectionType": null,
    "dataToBeSaved": true,
    "data": [
        {
            "questionTitle": "",
            "key": "bodyPartsWorkedOn",
            "answerList": [
                {
                    "type": "TEMPLATED_LIST_V2",
                    "openMode": true,
                    "fields": [
                        {
                            "type": "HEADER",
                            "key": "header",
                            "displayAdditionalInfo": false,
                            "targetKey": "bodyPart",
                            "textSize": "small"
                        },
                        {
                            "key": "comments",
                            "type": "TEXT_FIELD",
                            "placeholder": "Comment",
                            "textSize": "large"
                        }
                    ],
                    "defaultItemFields": [
                        {
                            "key": "suggestions",
                            "type": "SUGGESTION_LIST",
                            "textSize": "small",
                            "isSuggestion": true,
                            "recommendationKey": "prescriptionBodyPartsCateredTo",
                            "parentFieldKey": "bodyPart",
                            "targetFields": [
                                {
                                    "key": "bodyPart",
                                    "primaryTarget": true,
                                    "recommendationValue": "displayName",
                                    "internalValueKey": "code",
                                    "targetValueIsObject": true
                                },
                                {
                                    "key": "code",
                                    "primaryTarget": false,
                                    "recommendationValue": "code",
                                    "targetValueIsObject": false
                                },
                                {
                                    "key": "displayValue",
                                    "primaryTarget": false,
                                    "recommendationValue": "displayName",
                                    "targetValueIsObject": false
                                }
                            ]
                        },

                    ]
                }
            ]
        }
    ]
}

export const ActivityCategoryToDetailsKeyMap: { [key: string]: string } = {
    [ActivityCategory.MEAL]: "mealDetails",
    [ActivityCategory.WORKOUT]: "workoutDetails",
    [ActivityCategory.MEDICINE]: "medicineDetails",
    [ActivityCategory.MIND]: "mindDetails"
}

export const ActivityCategoryColorMap: { [key: string]: string } = {
    [ActivityCategory.MEAL]: "#f08840",
    [ActivityCategory.WORKOUT]: "#86cb6b",
    [ActivityCategory.MEDICINE]: "#52abf9",
    [ActivityCategory.MIND]: "#52abf9"
}
